import type { PlayerInstallSource } from './installSource';

// Admin User Types
export type AdminRoleType =
  | 'super_admin'
  | 'product_manager'
  | 'game_designer'
  | 'liveops_manager'
  | 'customer_support'
  | 'analyst';

export interface AdminRole {
  id: string;
  name: string;
  type: AdminRoleType;
}

export interface AdminUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  avatarUrl?: string;
  role: AdminRole;
  permissions: string[];
  status: AdminUserStatus;
  createdAt: string;
  lastLoginAt?: string;
}

export type AdminUserStatus = 'active' | 'inactive' | 'suspended';

/**
 * The App Store price of a product (admin products API). null = same as the
 * `price` every other platform gets. The Unity client shows Unity IAP's own
 * localized price, so this is for revenue valuation and the catalogue response.
 */
export interface ProductIosPrice {
  inr: number;
  usd: number;
}

export type PermissionAction =
  | 'list'
  | 'show'
  | 'create'
  | 'edit'
  | 'delete'
  | 'ban'
  | 'grant_item';

export interface ResourcePermissions {
  players?: PermissionAction[];
  analytics?: PermissionAction[];
  config?: PermissionAction[];
  liveops?: PermissionAction[];
  content?: PermissionAction[];
  economy?: PermissionAction[];
  tournaments?: PermissionAction[];
  experiments?: PermissionAction[];
  system?: PermissionAction[];
  admins?: PermissionAction[];
  player_data?: PermissionAction[];
}

export interface AdminRoleWithPermissions {
  id: string;
  name: string;
  type: AdminRoleType;
  description?: string;
  permissions: ResourcePermissions;
}

export interface AuditLogEntry {
  id: string;
  adminId?: string;
  adminEmail?: string;
  actionType: AuditActionType;
  targetType?: AuditTargetType;
  targetId?: string;
  targetName?: string;
  oldValue?: Record<string, unknown> | null;
  newValue?: Record<string, unknown> | null;
  metadata?: Record<string, unknown> | null;
  reason?: string | null;
  ipAddress: string;
  userAgent?: string | null;
  success: boolean;
  errorMessage?: string | null;
  createdAt: string;
}

export interface AuditLogQueryResponse {
  data: AuditLogEntry[];
  total: number;
  page: number;
  totalPages: number;
}

// Card-generation rating bounds (admin-configurable per rarity).
export interface RatingWindow {
  min: number;
  max: number;
}

export interface CardRatingBounds {
  // Keyed by rarity 0..3 (Rookie / Semi-Pro / Pro / Legendary).
  [rarity: string]: RatingWindow;
}

export interface CardGenerationConfig {
  cardRatingBounds: CardRatingBounds;
  // Odds (in %) per rarity 0..3 for cards made without a set rarity; absent on older backends.
  cardRarityWeights?: Record<string, number>;
  // 'global' = cardRarityWeights for everyone; 'division' = cardRarityWeightsByDivision by the
  // player's division. Absent on backends without division odds (treat as 'global').
  cardRarityMode?: 'global' | 'division';
  // Keyed by division tier "1".."21", each row odds (in %) per rarity "0".."3".
  cardRarityWeightsByDivision?: Record<string, Record<string, number>>;
  divisions?: Array<{ tier: number; name: string }>;
  updatedAt?: string;
}

// Fan Rating soft-clamp curve config (admin-configurable, GDD v1.00d).
export interface FrCurveConfig {
  maxStatFR: number;
  curveStrength: number;
  baseWinFactor: number;
  updatedAt?: string;
}

// Blitz Showdown config (admin-configurable, GDD v1.00d). The three tables are
// per-match (index 0..4 for tournament matches 1..5).
export interface BlitzConfig {
  coinBaseValue: number;
  cardDivisor: number;
  matN: number[];
  matchFactor: number[];
  ratingOffsets: number[];
  /** Match tokens one Blitz round costs (whole number 1-100, default 1). */
  entryCostTokens?: number;
  /** Player level Blitz opens at (whole number from 1, default 5). */
  unlockLevel?: number;
  updatedAt?: string;
}

// Player autoplay price: coins per autoplayed innings, served in /v1/config
// as `autoplay.coinCost`. null = not configured (the client uses its own price).
export interface AutoplayConfig {
  coinCost: number | null;
  updatedAt?: string;
}

// GDD batting rules document served in /v1/config as `battingRules`.
// isDefault = the server is serving its built-in copy (never edited, or reset).
export interface BattingRulesConfigResponse {
  battingRules: Record<string, unknown>;
  isDefault: boolean;
  updatedAt?: string;
}

// FR calculator — mirrors the backend FanRatingInput (server computes the result).
export interface FrCalcInput {
  won: boolean;
  draw: boolean;
  matchType: string;
  playerSingles?: number;
  playerDoubles?: number;
  playerTriples?: number;
  playerFours?: number;
  playerSixes?: number;
  playerExtras?: number;
  opponentSingles?: number;
  opponentDoubles?: number;
  opponentTriples?: number;
  opponentFours?: number;
  opponentSixes?: number;
  opponentExtras?: number;
  playerBallsSaved?: number;
  opponentBallsSaved?: number;
  playerWicketsTaken?: number;
  playerWicketsSaved?: number;
  opponentWicketsTaken?: number;
  opponentWicketsSaved?: number;
  runDifference?: number;
  playerTeamRating: number;
  opponentTeamRating: number;
  teamFactor: number;
  matchFactor?: number;
}

export interface FrCalcResult {
  fanRatingChange: number;
  config: { maxStatFR: number; curveStrength: number; baseWinFactor: number };
}

export interface AdminLoginCredentials {
  email: string;
  password: string;
}

export interface AdminLoginResponse {
  accessToken: string;
  refreshToken: string;
  user: AdminUser;
}

// Player Types
export interface Player {
  id: string;
  displayName: string;
  googlePlayId?: string;
  appleGameCenterId?: string;
  division: number;
  divisionName?: string;
  platform?: string;
  authProvider?: string;
  rating: number;
  level: number;
  experience: number;
  coins: number;
  gems: number;
  premiumGems: number;
  tickets: number;
  evoTokens?: number;
  matchTokens?: number;
  cardCount?: number;
  totalSpent?: number;
  totalMatches: number;
  totalWins: number;
  winRate: number;
  status: PlayerStatus;
  isActive: boolean;
  isGuest?: boolean;
  hasLinkedAccount?: boolean;
  loginCount: number;
  isBanned: boolean;
  banReason?: string;
  bannedUntil?: string;
  createdAt: string;
  lastLoginAt: string;
  // §2.4b — last active = max(latest match.createdAt, lastLoginAt). Better
  // signal than lastLoginAt alone for "is this player still playing".
  lastActiveAt?: string | null;
  /** Client build recorded on the player's last authenticated session. */
  lastBuildVersion?: number | null;
  /** Platform build number from x-build-number (Android versionCode, iOS CFBundleVersion). */
  lastBuildNumber?: number | null;
  lastAppVersion?: string | null;
  lastClientPlatform?: string | null;
  /** Where the player came from (AppsFlyer); absent when nothing was reported. */
  installSource?: PlayerInstallSource | null;
  lastConfigVersionId?: string | null;
  /** Label of lastConfigVersionId; null when the build is unmapped. */
  lastConfigVersion?: string | null;
  lastClientSeenAt?: string | null;
}

export type PlayerStatus = 'active' | 'banned' | 'deleted';

export interface PlayerSearchFilters {
  search?: string;
  division?: number;
  minLevel?: number;
  maxLevel?: number;
  status?: PlayerStatus;
  platform?: 'android' | 'ios';
}

export interface GrantItemRequest {
  itemType: 'coins' | 'gems' | 'premium_gems' | 'tickets' | 'player_card' | 'pack';
  itemId?: string;
  amount: number;
  reason: string;
}

export interface GrantItemsRequest {
  items: GrantItemRequest[];
  reason: string;
}

export interface BanPlayerRequest {
  banType: 'temporary' | 'permanent';
  reason: BanReason;
  reasonDetails?: string;
  durationDays?: number;
  evidence?: Record<string, unknown>;
}

export type BanReason =
  | 'cheating'
  | 'hacking'
  | 'abusive_behavior'
  | 'fraud'
  | 'multiple_accounts'
  | 'terms_violation'
  | 'suspicious_activity'
  | 'refund_abuse'
  | 'other';

export interface UnbanPlayerRequest {
  revocationReason: string;
}

export interface BanHistory {
  id: string;
  banType: string;
  reason: string;
  reasonDetails?: string;
  status: 'active' | 'expired' | 'revoked';
  expiresAt?: string;
  durationDays?: number;
  bannedByAdminEmail: string;
  revokedByAdminEmail?: string;
  revokedAt?: string;
  revocationReason?: string;
  createdAt: string;
}

export interface PlayerEconomy {
  userId: string;
  coins: number;
  evoTokens: number;
  matchTokens: number;
  premiumCurrency: number;
  fanRating: number;
  level: number;
  totalXp: number;
  currentLevelXp: number;
  matchesPlayed: number;
  matchesWon: number;
  matchesLost: number;
  totalRunsScored: number;
  totalWicketsTaken: number;
  consecutiveLoginDays: number;
  lastMatchAt?: string;
  lastLoginAt?: string;
}

export interface UpdateEconomyRequest {
  coins?: number;
  evoTokens?: number;
  matchTokens?: number;
  premiumCurrency?: number;
  fanRating?: number;
  totalXp?: number;
  level?: number;
  reason: string;
}

export interface GrantCurrencyRequest {
  currencyType: 'coins' | 'evoTokens' | 'matchTokens' | 'premiumCurrency' | 'fanRating';
  amount: number;
  reason: string;
}

export interface CurrencyTransaction {
  id: string;
  currencyType: string;
  amount: number;
  source: string;
  previousBalance: number;
  newBalance: number;
  metadata?: Record<string, unknown>;
  createdAt: string;
}

// Analytics Types
export interface DashboardStats {
  dau: number;
  mau: number;
  newUsers: number;
  revenue: number;
  arpu: number;
  avgSessionDuration: number;
  matchesPlayed: number;
  retentionD1: number;
  retentionD7: number;
  retentionD30: number;
}

export interface DivisionDistribution {
  divisionId: number;
  divisionName: string;
  tierName: string;
  playerCount: number;
  /** Total matches played by users currently in this division. */
  totalMatches: number;
  /** Aggregate win % for the division (0-100). 0 when no matches played. */
  winPercentage: number;
  percentage: number;
}

export interface EconomyData {
  totalCoinsInCirculation: number;
  totalGemsInCirculation: number;
  totalEvoTokensInCirculation?: number;
  totalMatchTokensInCirculation?: number;
  avgCoinsPerUser: number;
  avgGemsPerUser: number;
  avgEvoTokensPerUser?: number;
  avgMatchTokensPerUser?: number;
  /** @deprecated use flows.coins.sources */
  coinSources: CurrencyFlow[];
  /** @deprecated use flows.coins.sinks */
  coinSinks: CurrencyFlow[];
  coinsInflowToday?: number;
  coinsOutflowToday?: number;
  netCoinFlow?: number;
  /**
   * Per-currency sources / sinks / inflow / outflow / netFlow for all 4
   * currency types. Backend may omit this on older deploys — fall back to
   * the deprecated coin-only fields above.
   */
  flows?: CurrencyFlowsMap;
}

export interface CurrencyFlow {
  source: string;
  amount: number;
  percentage: number;
}

export interface CurrencyFlowSummary {
  sources: CurrencyFlow[];
  sinks: CurrencyFlow[];
  inflow: number;
  outflow: number;
  netFlow: number;
}

export type CurrencyFlowsMap = Record<CurrencyType, CurrencyFlowSummary>;

export interface RevenueData {
  date: string;
  revenue: number;
  transactions: number;
  uniquePayers: number;
}

// Configuration Types
export interface FeatureFlags {
  [key: string]: boolean;
}

export interface DivisionConfig {
  tier: number;
  name: string;
  minRating: number;
  maxRating: number;
  rewardMultiplier: number;
  dropBonus: number;
}

export interface MaintenanceStatus {
  enabled: boolean;
  message: string;
}

// LiveOps Types
export interface NotificationPayload {
  title: string;
  body: string;
  targeting: {
    type: 'all' | 'segment' | 'userList';
    segment?: PlayerSegment;
    userIds?: string[];
  };
  scheduling: {
    type: 'immediate' | 'scheduled';
    scheduledAt?: string;
  };
  data?: Record<string, string>;
}

export interface PlayerSegment {
  minDivision?: number;
  maxDivision?: number;
  minLevel?: number;
  maxLevel?: number;
  platform?: 'android' | 'ios';
  lastActiveWithin?: number; // days
  minSpent?: number;
  maxSpent?: number;
}

export type LifecycleTriggerKey =
  | 'ftue_done_no_match'
  | 'first_match_unfinished'
  | 'inactive_d1'
  | 'inactive_d3'
  | 'inactive_d7'
  | 'ftue_incomplete';

/** One trigger on GET /admin/liveops/lifecycle-push/status. */
export interface LifecyclePushTriggerStatus {
  key: LifecycleTriggerKey;
  title: string;
  body: string;
  /** First IST hour the trigger may be sent (inactive_d1 starts at 18). */
  earliestIstHour: number;
  /** From the latest automatic run that ran this trigger (last 2 days). */
  latest: { at: string; qualified: number; skippedLocal: number } | null;
}

export interface LifecyclePushStatus {
  autoEnabled: boolean;
  dailyCap: number;
  usedToday: number;
  maxDailyCap: number;
  /** Null while automatic sending is off. */
  nextRunAt: string | null;
  /** Super admin: may raise the cap and switch automatic sending on. */
  canRaise: boolean;
  triggers: LifecyclePushTriggerStatus[];
}

/** Per IST day and trigger. qualified/skipped are distinct players (approximate). */
export interface LifecyclePushStatsRow {
  istDay: string;
  trigger: LifecycleTriggerKey;
  qualified: number;
  skippedLocal: number;
  skippedCap: number;
  sent: number;
  delivered: number;
  noDevice: number;
  failed: number;
  expired: number;
  /** Not sent: over the per-player push limit. Absent from an older API. */
  limited?: number;
  returned24h: number;
  /**
   * Of delivered pushes: back within 2 h, started a match within 24 h, back
   * within 24 h with no match push in between, and finished the tutorial within
   * 24 h. Absent from an older API: show a dash, not a zero.
   */
  returned2h?: number;
  matchStarted24h?: number;
  returnedNoMatchPush24h?: number;
  tutorialDone24h?: number;
}

/**
 * One copy on GET /admin/liveops/lifecycle-push/variants, most delivered first.
 * variant looks like `inactive_d3.lost_last.hi`, `inactive_d3.lost_last.control`
 * or null (pushes sent before variants existed). Counts cover delivered pushes.
 */
export interface LifecyclePushVariantRow {
  trigger: string;
  variant: string | null;
  language: string | null;
  sent: number;
  delivered: number;
  returned2h: number;
  returned24h: number;
  matchStarted24h: number;
  returnedNoMatchPush24h: number;
  tutorialDone24h: number;
}

/** A sample or an automatic run that sent something. */
export interface LifecyclePushRun {
  id: string;
  kind: 'auto' | 'sample';
  adminEmail: string | null;
  startedAt: string;
  triggers: LifecycleTriggerKey[];
  claimed: number;
  delivered: number;
  failed: number;
  cancelled: number;
  returned24h: number;
}

/** Answer to a sample send. */
export interface LifecyclePushRunReport {
  runId: string | null;
  kind: 'auto' | 'sample';
  perTrigger: Partial<
    Record<
      LifecycleTriggerKey,
      { qualified: number; skippedLocal: number; skippedCap: number; claimed: number }
    >
  >;
  claimed: number;
}

export interface NotificationHistory {
  id: string;
  title: string;
  body: string;
  recipientCount: number;
  sentBy: string;
  sentAt: string;
  status: 'sent' | 'scheduled' | 'failed' | 'cancelled';
}

// Tournament Types
export interface Tournament {
  id: string;
  name: string;
  description?: string;
  type: TournamentType;
  tier: number; // Backend uses 1-5, not string
  status: TournamentStatus;
  gameMode: string;
  overs: number;
  maxParticipants: number;
  currentParticipants: number;
  minDivisionTier?: number;
  entryFee: number; // Backend uses single number for coins only
  registrationStart: string;
  registrationEnd: string;
  startTime: string;
  createdAt: string;
}

export type TournamentType = 'knockout' | 'league' | 'swiss' | 'round_robin';
export type TournamentTier = 1 | 2 | 3 | 4 | 5; // Numeric values: 1=bronze, 2=silver, 3=gold, 4=platinum, 5=diamond
export type TournamentStatus =
  | 'upcoming'
  | 'registration'
  | 'in_progress'
  | 'completed'
  | 'cancelled';

export interface TournamentReward {
  rank: number;
  coins?: number;
  gems?: number;
  xp?: number;
  cardPacks?: number;
}

export interface CreateTournamentRequest {
  name: string;
  description?: string;
  type: TournamentType;
  tier: number; // 1-5
  gameMode: string;
  overs: number;
  maxParticipants: number;
  minDivisionTier?: number;
  entryFee?: number; // Coins only
  registrationStart: string;
  registrationEnd: string;
  startTime: string;
  rewards?: {
    positions: TournamentReward[];
    participation?: {
      coins?: number;
      xp?: number;
    };
  };
  settings?: {
    autoMatchmaking?: boolean;
    matchTimeoutMinutes?: number;
    allowSubstitutes?: boolean;
  };
}

// Audit Log Types
export interface AuditLog {
  id: string;
  adminId: string;
  adminEmail: string;
  action: AuditActionType;
  targetType: AuditTargetType;
  targetId?: string;
  details: Record<string, unknown>;
  ipAddress?: string;
  userAgent?: string;
  createdAt: string;
}

// Action / target types are backed by Postgres enums that grow over time
// (login, admin_update, cm_national_team_create, etc.). Keeping them as plain
// strings on the frontend avoids drift; render-time mappings live in the
// pages that need them.
export type AuditActionType = string;
export type AuditTargetType = string;

// Webhook Types
export type WebhookEventType =
  | 'user.registered'
  | 'user.login'
  | 'user.logout'
  | 'user.profile_updated'
  | 'user.account_linked'
  | 'purchase.initiated'
  | 'purchase.completed'
  | 'purchase.failed'
  | 'purchase.refunded'
  | 'subscription.started'
  | 'subscription.renewed'
  | 'subscription.cancelled'
  | 'subscription.expired'
  | 'match.started'
  | 'match.completed'
  | 'match.abandoned'
  | 'tournament.joined'
  | 'tournament.completed'
  | 'tournament.reward_claimed'
  | 'progression.level_up'
  | 'progression.achievement_unlocked'
  | 'progression.division_promoted'
  | 'progression.division_demoted'
  | 'economy.coins_earned'
  | 'economy.coins_spent'
  | 'economy.gems_earned'
  | 'economy.gems_spent'
  | 'card.acquired'
  | 'card.upgraded'
  | 'card.evolved'
  | 'season_pass.purchased'
  | 'season_pass.tier_unlocked'
  | 'daily.login'
  | 'daily.challenge_completed'
  | 'daily.reward_claimed';

export type WebhookStatus = 'active' | 'inactive' | 'suspended';
export type WebhookAuthType = 'none' | 'api_key' | 'secret_key' | 'both';
export type WebhookLogStatus = 'pending' | 'success' | 'failed' | 'retrying';

export interface Webhook {
  id: string;
  name: string;
  description?: string;
  url: string;
  authType: WebhookAuthType;
  apiKey?: string;
  secretKey?: string;
  apiKeyHeader: string;
  signatureHeader: string;
  events: WebhookEventType[];
  status: WebhookStatus;
  timeoutMs: number;
  retryCount: number;
  retryDelayMs: number;
  maxConsecutiveFailures: number;
  consecutiveFailures: number;
  lastSuccessAt?: string;
  lastFailureAt?: string;
  totalSuccessCount: number;
  totalFailureCount: number;
  metadata?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface CreateWebhookRequest {
  name: string;
  description?: string;
  url: string;
  authType?: WebhookAuthType;
  apiKey?: string;
  secretKey?: string;
  apiKeyHeader?: string;
  signatureHeader?: string;
  events: WebhookEventType[];
  timeoutMs?: number;
  retryCount?: number;
  retryDelayMs?: number;
  maxConsecutiveFailures?: number;
  metadata?: Record<string, unknown>;
}

export interface UpdateWebhookRequest extends Partial<CreateWebhookRequest> {
  status?: WebhookStatus;
}

export interface WebhookLog {
  id: string;
  webhookId: string;
  eventType: WebhookEventType;
  userId?: string;
  referenceId?: string;
  referenceType?: string;
  status: WebhookLogStatus;
  requestUrl: string;
  requestMethod: string;
  requestHeaders?: Record<string, string>;
  requestBody: Record<string, unknown>;
  responseStatusCode?: number;
  responseHeaders?: Record<string, string>;
  responseBody?: string;
  errorMessage?: string;
  errorCode?: string;
  durationMs?: number;
  attemptNumber: number;
  maxAttempts: number;
  nextRetryAt?: string;
  completedAt?: string;
  createdAt: string;
}

export interface WebhookStats {
  totalDeliveries: number;
  successCount: number;
  failureCount: number;
  successRate: number;
  averageDurationMs: number;
  lastDeliveryAt: string | null;
}

export interface WebhookEventInfo {
  events: WebhookEventType[];
  descriptions: Record<WebhookEventType, string>;
}

// API Response Types
export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ApiResponse<T> {
  data: T;
  message?: string;
}

export interface ApiError {
  statusCode: number;
  message: string;
  error?: string;
}

// Account Transfer Types
export type AuthProvider =
  | 'GUEST'
  | 'GOOGLE_PLAY_GAMES'
  | 'APPLE_GAME_CENTER'
  | 'guest'
  | 'google_play_games'
  | 'apple_game_center';
export type Platform = 'ANDROID' | 'IOS' | 'UNITY_EDITOR';

export interface TransferAccountRequest {
  targetProvider: AuthProvider;
  targetPlatform?: Platform;
  googlePlayGamesId?: string;
  appleGameCenterId?: string;
  deviceId?: string;
  reason: string;
  clearOldPlatformIds?: boolean;
}

export interface TransferAccountResponse {
  success: boolean;
  userId: string;
  previousProvider: AuthProvider;
  newProvider: AuthProvider;
  previousPlatform?: Platform;
  newPlatform?: Platform;
  message: string;
  transferredAt: string;
}

export interface MergeAccountRequest {
  loserUserId: string;
  reason?: string;
}

export interface MergeAccountResponse {
  survivorId: string;
  loserId: string;
  tablesTouched: number;
}

export interface TransferHistory {
  id: string;
  userId: string;
  fromProvider: AuthProvider;
  toProvider: AuthProvider;
  fromPlatform?: Platform;
  toPlatform?: Platform;
  adminEmail: string;
  reason: string;
  createdAt: string;
}

// Player Match Types
export interface AiAnalyticsData {
  matchId?: string | null;
  bv?: number | null;
  os?: string | null;
  aigrp?: string | null;
  aidiff?: number | null;
  ovr?: number | null;
  userteam?: string | null;
  aiteam?: string | null;
  userscr?: string | null;
  aiscr?: string | null;
  userballs?: string | null;
  aiballs?: string | null;
  verdict?: number | null;
  userFour?: number | null;
  userSix?: number | null;
  aidotball?: number | null;
  aiFour?: number | null;
  aiSix?: number | null;
  mfb?: number | null;
  tour?: number | null;
  unqId?: string | null;
  apbatuser?: number | null;
  apbowluser?: number | null;
  wku?: string | null;
  wka?: string | null;
  usermiss?: string | null;
  aimiss?: string | null;
  userRunrate?: number | null;
  aiRunrate?: number | null;
  ptr?: number | null;
  otr?: number | null;
  divisions?: number | null;
  bybDataUser?: string | null;
  bybDataAi?: string | null;
}

// ==================== AI Analytics Aggregation Types ====================

export interface AiBreakdownItem {
  label: string;
  value: string | number;
  matches: number;
  wins: number;
  losses: number;
  ties: number;
  winRate: number;
}

export interface AiDistributionItem {
  label: string;
  value: string | number;
  count: number;
  percentage: number;
}

export interface AiTeamCount {
  team: string;
  count: number;
}

export interface AiBattingOrderStats {
  batFirstMatches: number;
  batFirstWins: number;
  batFirstWinRate: number;
  bowlFirstMatches: number;
  bowlFirstWins: number;
  bowlFirstWinRate: number;
}

export interface AiAutoPlayStats {
  totalMatches: number;
  autoPlayBatMatches: number;
  autoPlayBowlMatches: number;
  autoPlayBatPercent: number;
  autoPlayBowlPercent: number;
}

export interface UserAiAnalyticsResponse {
  summary: {
    totalMatches: number;
    wins: number;
    losses: number;
    ties: number;
    winRate: number;
    avgUserRunRate: number;
    avgAiRunRate: number;
  };
  byDifficulty: AiBreakdownItem[];
  byVerdict: AiBreakdownItem[];
  byAiGroup: AiBreakdownItem[];
  byDivision: AiBreakdownItem[];
  battingStats: {
    totalFours: number;
    totalSixes: number;
    avgFoursPerMatch: number;
    avgSixesPerMatch: number;
    avgUserDotBalls: number;
  };
  bowlingStats: {
    avgAiDotBalls: number;
    avgAiFours: number;
    avgAiSixes: number;
  };
  teamRatings: {
    avgPTR: number;
    avgOTR: number;
  };
  battingOrder: AiBattingOrderStats;
  topUserTeams: AiTeamCount[];
  topAiTeams: AiTeamCount[];
  autoPlay: AiAutoPlayStats;
}

export interface GlobalAiAnalyticsResponse {
  overview: {
    totalMatches: number;
    uniquePlayers: number;
    avgMatchesPerPlayer: number;
    overallWinRate: number;
    avgUserDotBalls: number;
    avgPTR: number;
    avgOTR: number;
  };
  difficultyDistribution: AiDistributionItem[];
  winRateByDifficulty: AiBreakdownItem[];
  winRateByAiGroup: AiBreakdownItem[];
  winRateByDivision: AiBreakdownItem[];
  topUserTeams: AiTeamCount[];
  topAiTeams: AiTeamCount[];
  battingOrder: AiBattingOrderStats;
  runRateByDifficulty: {
    difficulty: string;
    avgUserRunRate: number;
    avgAiRunRate: number;
    matches: number;
  }[];
  osDistribution: AiDistributionItem[];
  buildVersionDistribution: AiDistributionItem[];
  autoPlay: AiAutoPlayStats;
}

export interface PlayerMatch {
  id: string;
  matchType: string;
  overs: number;
  status: 'in_progress' | 'completed' | 'abandoned';
  result?: 'win' | 'loss' | 'draw';
  opponentName: string;
  userRuns: number;
  userWickets: number;
  opponentRuns: number;
  opponentWickets: number;
  xpEarned?: number;
  coinsEarned?: number;
  createdAt: string;
  completedAt?: string;
  aiAnalytics?: AiAnalyticsData;
  dF?: number | null;
  dFLevel?: string | null;
}

export interface PlayerMatchStats {
  totalMatches: number;
  wins: number;
  losses: number;
  draws: number;
  winRate: number;
  totalRunsScored: number;
  totalWicketsTaken: number;
  highestScore: number;
  averageScore: number;
}

export interface PlayerMatchHistory {
  stats: PlayerMatchStats;
  recentMatches: PlayerMatch[];
  total: number;
}

// Player Squad Types
export interface PlayerSquadSlot {
  position: string;
  role: string;
  playerCardId?: string;
  playerId?: string;
  playerIndex?: number;
  firstName?: string;
  secondName?: string;
  rarity?: number;
  overallRating?: number;
  playerType?: number;
  playerLevel?: number;
  evolutionLevel?: number;
  bowlerType?: number;
  battingAggregate?: number;
  bowlingAggregate?: number;
  fieldingAggregate?: number;
  physicalAggregate?: number;
  isWicketKeeper?: boolean;
  isLocked?: boolean;
  matchesPlayed?: number;
  battingStats?: CardBattingStats;
  bowlingStats?: CardBowlingStats;
}

export interface PlayerSquad {
  id: string;
  squadType: 'home' | 'away';
  name: string;
  totalRating: number;
  playerCount: number;
  slots: PlayerSquadSlot[];
  createdAt: string;
}

export interface PlayerSquadsResponse {
  homeSquad?: PlayerSquad;
  awaySquad?: PlayerSquad;
  totalPlayerCards: number;
}

// Player Card Types
export interface CardBattingStats {
  totalRuns: number;
  totalBallsFaced: number;
  totalFours: number;
  totalSixes: number;
  halfCenturies: number;
  centuries: number;
  bestScore: number;
  inningsPlayed: number;
}

export interface CardBowlingStats {
  totalBallsBowled: number;
  totalRunsGiven: number;
  totalWickets: number;
  totalMaidens: number;
  bestBowlingWickets: number;
  fiveWicketHauls: number;
}

export interface PlayerCard {
  id: string;
  playerId: string;
  playerIndex: number;
  firstName?: string;
  secondName?: string;
  rarity: number;
  overallRating: number;
  playerType: number;
  playerLevel: number;
  evolutionLevel: number;
  bowlerType: number;
  battingAggregate: number;
  bowlingAggregate: number;
  fieldingAggregate: number;
  physicalAggregate: number;
  isWicketKeeper?: boolean;
  isInSquad: boolean;
  isLocked: boolean;
  createdAt: string;
  matchesPlayed?: number;
  battingStats?: CardBattingStats;
  bowlingStats?: CardBowlingStats;
}

export interface PlayerCardsResponse {
  cards: PlayerCard[];
  total: number;
}

// Player Device Types
export interface PlayerDevice {
  id: string;
  fcmToken: string;
  platform: 'android' | 'ios';
  deviceModel?: string;
  osVersion?: string;
  appVersion?: string;
  isActive: boolean;
  lastActiveAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PlayerDevicesResponse {
  devices: PlayerDevice[];
  totalDevices: number;
  activeDevices: number;
}

// Player Full Details (combined response)
export interface PlayerFullDetails extends Player {
  economy?: PlayerEconomy;
  matchHistory?: PlayerMatchHistory;
  squads?: PlayerSquadsResponse;
}

// Purchase Search Types
export type PurchaseClassification = 'real' | 'sandbox' | 'internal_tester' | 'editor';

export interface PurchaseSearchFilters {
  orderId?: string;
  userId?: string;
  userName?: string;
  productId?: string;
  platform?: Platform;
  status?: 'pending' | 'completed' | 'failed' | 'refunded';
  classification?: PurchaseClassification;
  includeUserInfo?: boolean;
  startDate?: string;
  endDate?: string;
}

export interface Purchase {
  id: string;
  orderId: string;
  userId: string;
  productId: string;
  amount: number;
  currency: string;
  platform: Platform;
  purchaseType: 'consumable' | 'subscription';
  status: 'pending' | 'completed' | 'failed' | 'refunded' | 'cancelled';
  purchaseClassification?: PurchaseClassification;
  verificationData?: Record<string, unknown>;
  userDisplayName?: string;
  userPlatform?: string;
  /** When the purchase was marked refunded (ISO string); null/absent if it was not. */
  refundedAt?: string | null;
  /** What the player paid in the store, in millionths of `storeCurrency` (bigint as a string). */
  storePriceMicros?: string | null;
  storeCurrency?: string | null;
  storeRegion?: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Counts from `POST /admin/purchases/refunds/voided-sync` (Google voided-purchase sweep). */
export interface VoidedSyncCounts {
  pages: number;
  listed: number;
  refunded: number;
  alreadyRefunded: number;
  notFound: number;
  notCompleted: number;
  errors: number;
}

/** POST /admin/purchases/:id/recheck-google. purchaseState: 0 purchased, 1 cancelled, 2 pending. */
export interface GoogleRecheckResult {
  credited: boolean;
  purchaseId: string;
  userId: string;
  productId: string;
  status: string;
  orderId?: string;
  purchaseState?: number;
  error?: string;
  itemsGranted?: Record<string, unknown>;
}

export interface PurchaseTester {
  id: string;
  platform: 'apple' | 'google' | 'any';
  identifier: string;
  label: 'qa_team' | 'internal' | 'partner';
  notes?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Purchase Analytics & Subscription Types
export interface RevenueByCurrencyRow {
  currency: string;
  revenue: number;
  transactions: number;
}

export interface RevenuePlatformCurrencyRow {
  platform: string;
  currency: string;
  revenue: number;
  transactions: number;
}

export interface RevenuePurchaseTypeCurrencyRow {
  type: string;
  currency: string;
  revenue: number;
  transactions: number;
}

export interface RevenueSummary {
  /** @deprecated naive cross-currency sum — use byCurrency */
  totalRevenue: number;
  totalTransactions: number;
  /** @deprecated naive cross-currency average — derive per-currency */
  averageTransactionValue: number;
  uniqueBuyers: number;
  newBuyers: number;
  repeatBuyers: number;
  conversionRate: number;
  byCurrency?: RevenueByCurrencyRow[];
  // New backend shape is an array; old shape was Record. Accept both during
  // the deploy window — frontend code normalises before render.
  byPlatform:
    | RevenuePlatformCurrencyRow[]
    | Record<string, { revenue: number; transactions: number }>;
  byPurchaseType:
    | RevenuePurchaseTypeCurrencyRow[]
    | Record<string, { revenue: number; transactions: number }>;
}

export interface RevenueTimeSeries {
  period: string;
  /** ISO-4217 currency code for this row. Optional during deploy bridge. */
  currency?: string;
  revenue: number;
  transactions: number;
  uniqueBuyers: number;
  averageValue: number;
}

export interface RevenueTopProduct {
  productId: string;
  productName?: string;
  /**
   * Currency this row's revenue is denominated in. The backend groups top
   * products by (productId x currency) because nothing converts between
   * currencies — a product sold in two currencies is two rows.
   */
  currency?: string;
  revenue: number;
  transactions?: number;
  count?: number;
  uniqueBuyers?: number;
}

export interface RevenueAnalyticsResponse {
  summary: RevenueSummary;
  timeSeries: RevenueTimeSeries[];
  topProducts: RevenueTopProduct[];
}

export interface ActiveSubscription {
  purchaseId: string;
  userId: string;
  productId: string;
  productName?: string;
  platform: string;
  startDate: string;
  expiryDate: string;
  isAutoRenewing: boolean;
  daysRemaining: number;
}

export interface SubscriptionSummary {
  totalActiveSubscriptions: number;
  totalRevenue: number;
  churnRate: number;
  averageSubscriptionLength: number;
  byTier: Record<string, number>;
  expiringThisWeek: number;
  expiringThisMonth: number;
}

export interface UserPurchaseStats {
  userId: string;
  totalSpent: number;
  totalPurchases: number;
  firstPurchaseDate?: string;
  lastPurchaseDate?: string;
  averagePurchaseValue: number;
  hasActiveSubscription: boolean;
  subscriptionExpiryDate?: string;
  topProducts: Array<{ productId: string; count: number; totalSpent: number }>;
}

// Daily Deals Types
export type DealType =
  | 'free_drop'
  | 'free_coin_drop'
  | 'free_card_drop'
  | 'player_of_day'
  | 'legend_of_week';
export type CurrencyType = 'coins' | 'matchTokens' | 'evoTokens' | 'premiumCurrency';
export type PaymentMethod =
  | 'free'
  | 'coins'
  | 'matchTokens'
  | 'evoTokens'
  | 'premiumCurrency'
  | 'iap';

export interface DealPricing {
  coins?: number;
  matchTokens?: number;
  evoTokens?: number;
  premiumCurrency?: number;
}

export interface DealRewards {
  dropWeights?: {
    coins: number;
    matchTokens: number;
    evoTokens: number;
    cards: number;
  };
  cardRarity?: number;
  cardCount?: number;
  featuredPlayerIndex?: number; // Admin-set player index — see GET /admin/config/roster-size for the current upper bound
  // Rarity probability weights (admin-configurable, percentage-based)
  // e.g. { 0: 70, 1: 20, 2: 9, 3: 1 } for free_drop cards
  rarityWeights?: Record<number, number>;
  // Currency rewards (can be granted by any deal)
  coins?: number;
  matchTokens?: number;
  evoTokens?: number;
  premiumCurrency?: number;
  // §2.7 base drop amounts (free_drop only). When the deal rolls a
  // currency reward, this controls the amount granted before the
  // per-division scaling kicks in.
  baseAmounts?: {
    coins?: number;
    matchTokens?: number;
    evoTokens?: number;
    cards?: number;
  };
  // §2.7 evo-token amount per division tier (1=Rookie III … 21=Legendary I).
  // Map keys are stringified tier numbers; missing keys fall back to the
  // hardcoded EVO_TOKENS_BY_DIVISION table in
  // daily-drop-reward.service.ts.
  evoTokensByDivision?: Record<string, number>;
}

// Risks & Hacks page: the six player risk lists (GET /admin/analytics/risk/lists/:list).
export type RiskListName =
  | 'suspiciousWinRates'
  | 'economyOutliers'
  | 'rapidProgression'
  | 'largeTransactions'
  | 'unpurchasedPremium'
  | 'divisionMatchMismatch';

export interface SuspiciousPlayer {
  /** Set where a player can appear more than once (largeTransactions: the transaction id). */
  id?: string;
  userId: string;
  displayName: string;
  platform: string;
  metric: string;
  value: number;
  threshold: number;
  detail: string;
}

export interface RiskListPage {
  /** Every flagged player in the window; absent for largeTransactions, a ranking with no threshold. */
  total?: number;
  /** Rows the server can page through: total, or its first 2,000 when total is larger. */
  listed: number;
  rows: SuspiciousPlayer[];
}

// §6.5 Suspect-match (cheat-detection) types — surfaced on the Risks & Hacks page.
export interface SuspectMatchItem {
  id: string;
  userId: string;
  userDisplayName?: string | null;
  matchType: string;
  overs: number;
  userRuns: number;
  userOvers: number;
  opponentRuns: number;
  opponentOvers: number;
  durationSeconds: number;
  suspectedCheatReason: string;
  createdAt: string;
  completedAt?: string | null;
}

export interface SuspectMatchesByReason {
  reason: string;
  count: number;
}

export interface SuspectMatchesResponse {
  /** Every flagged match in the window; `matches` holds at most the requested limit. */
  total: number;
  uniqueUsers: number;
  byReason: SuspectMatchesByReason[];
  matches: SuspectMatchItem[];
}

// Client earns (/economy/earn, /progression/earn) above the reward config's
// IST-day limit. Granted anyway; flagged for review (backend PROGRESS decision 13).
export interface EarnLimitFlagItem {
  id: string;
  userId: string;
  userDisplayName?: string | null;
  istDay: string;
  route: 'economy' | 'progression' | 'match_result' | string;
  source: string;
  currency: string;
  dayTotal: number;
  grantCount: number;
  dayLimit: number;
  limitBasis: string;
  lastAmount: number;
  platform?: string | null;
  appVersion?: string | null;
  buildVersion?: string | null;
  firstFlaggedAt: string;
  lastFlaggedAt: string;
}

export interface EarnLimitFlagsByKey {
  route: string;
  source: string;
  currency: string;
  count: number;
  uniqueUsers: number;
}

export interface EarnLimitFlagsResponse {
  /** Every flag row in the window; `flags` holds at most the requested limit. */
  total: number;
  uniqueUsers: number;
  byKey: EarnLimitFlagsByKey[];
  flags: EarnLimitFlagItem[];
}

// Rare-or-better POST /v1/player-cards/generate calls, audit only (backend decision M5 phase A).
export interface CardGenerateAuditItem {
  id: string;
  userId: string;
  userDisplayName?: string | null;
  istDay: string;
  route: string;
  /** 1 Rare, 2 Epic, 3 Legendary. */
  rarity: number;
  /** True when the request body carried a rarity. */
  chosenByClient: boolean;
  callCount: number;
  withDropCount: number;
  rejectedCount: number;
  platform: string | null;
  appVersion: string | null;
  buildVersion: string | null;
  buildNumber: number | null;
  firstAt: string;
  lastAt: string;
}

export interface CardGenerateAuditByRarity {
  rarity: number;
  chosenByClient: boolean;
  calls: number;
  uniqueUsers: number;
  withDrop: number;
  rejected: number;
}

export interface CardGenerateAuditByBuild {
  /** buildNumber, else buildVersion, else "unknown". */
  build: string;
  platform: string | null;
  calls: number;
  uniqueUsers: number;
}

export interface CardGenerateAuditResponse {
  /** Every audit row in the window; `rows` holds at most the requested limit. */
  total: number;
  uniqueUsers: number;
  totalCalls: number;
  byRarity: CardGenerateAuditByRarity[];
  /** Top 50 builds by calls. */
  byBuild: CardGenerateAuditByBuild[];
  rows: CardGenerateAuditItem[];
}

export interface DailyDealConfig {
  id: string;
  dealType: DealType;
  name: string;
  description: string;
  gemCost: number | null; // Legacy - use pricing instead
  pricing?: DealPricing | null; // Multi-currency pricing
  iapCostUsd: number | null;
  iapProductId: string | null;
  cooldownHours: number;
  rewards: DealRewards;
  isActive: boolean;
  sortOrder: number;
  featuredPlayerIndex?: number | null; // Admin-set player index — see GET /admin/config/roster-size for the current upper bound
  /**
   * Config version this row is scoped to. Null means shared — every build sees
   * it. A non-null value REPLACES the shared row of the same dealType for
   * clients that resolve to that config version.
   */
  configVersionId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DailyDealAnalytics {
  dealType: string;
  totalClaims: number;
  freeClaims: number;
  coinClaims: number;
  matchTokenClaims: number;
  evoTokenClaims: number;
  gemClaims: number;
  iapClaims: number;
  totalCoinsSpent: number;
  totalMatchTokensSpent: number;
  totalEvoTokensSpent: number;
  totalGemsSpent: number;
  totalRevenue: number;
  uniqueUsers: number;
}

export interface ResetCooldownRequest {
  userId: string;
  dealType: DealType;
}

export interface RegenerateSeedRequest {
  date?: string; // YYYY-MM-DD format, optional (defaults to today)
}

export interface UpdateDailyDealConfigRequest {
  gemCost?: number; // Legacy - use pricing instead
  pricing?: DealPricing;
  iapCostUsd?: number;
  iapProductId?: string;
  cooldownHours?: number;
  rewards?: DealRewards;
  isActive?: boolean;
  name?: string;
  description?: string;
  sortOrder?: number;
  featuredPlayerIndex?: number;
}

// Admin Purchase History
export interface DailyDealPurchase {
  id: string;
  userId: string;
  userDisplayName?: string;
  dealType: DealType;
  purchasedAt: string;
  paymentMethod: PaymentMethod;
  gemCost: number | null; // Legacy
  currencyType?: CurrencyType | null;
  currencyCost?: number | null;
  iapTransactionId: string | null;
  rewardsGranted: Record<string, unknown>;
}

export interface DailyDealPurchaseHistoryResponse {
  data: DailyDealPurchase[];
  total: number;
  page: number;
  limit: number;
}

export interface PurchaseHistoryFilters {
  userId?: string;
  userName?: string;
  startDate?: string;
  endDate?: string;
  dealType?: DealType;
  paymentMethod?: PaymentMethod;
  page?: number;
  limit?: number;
}

// User Daily Deals Status
export interface UserDealStatus {
  dealType: DealType;
  isOnCooldown: boolean;
  cooldownRemaining?: number;
  nextAvailableAt?: string;
  totalClaims: number;
  lastClaimAt?: string;
}

// Matches backend DealPurchaseHistoryItemDto (no userId/userDisplayName)
export interface UserRecentPurchase {
  id: string;
  dealType: DealType;
  purchasedAt: string;
  paymentMethod: PaymentMethod;
  gemCost?: number | null;
  currencyType?: CurrencyType | null;
  currencyCost?: number | null;
  iapTransactionId?: string | null;
  rewardsGranted: Record<string, unknown>;
}

export interface UserDailyDealsStatus {
  userId: string;
  userDisplayName?: string;
  dealStatuses: UserDealStatus[];
  totalClaims: number;
  recentPurchases: UserRecentPurchase[];
}

// Export Query
export interface ExportFilters {
  fromDate?: string;
  toDate?: string;
  dealType?: DealType;
}

// Matchday Director
export interface MatchdayDirectorProfile {
  totalMatches: number;
  wins: number;
  losses: number;
  winRate: number;
  avgRunRate: number;
  avgEconomy: number;
  totalRunsScored: number;
  totalRunsConceded: number;
  totalWicketsTaken: number;
  totalWicketsLost: number;
  recentForm: string;
  recentWinRate: number;
  consecutiveLosses: number;
  daysSinceLastMatch: number;
}

export interface DirectorFactors {
  baseDifficulty: number;
  winLossFactor: number;
  churnFactor: number;
  churnWeight: number;
  difficultyFactor: number;
  difficultyLevel: string;
  currentDifficulty: number;
  suggestedDifficulty: number;
  targetDifficulty: number;
  difficultyMode: number;
  difficultyModeLabel: string;
}

export interface OpponentAdjustments {
  runRateRange: [number, number];
  economyRange: [number, number];
  teamRatingRange: [number, number];
}

export interface ChurnComponents {
  consecutiveLosses: number;
  recentWinRate: number;
  inactivity: number;
  sessionFrustration: number;
}

export interface ChurnAssessment {
  churnRiskScore: number;
  components: ChurnComponents;
  isChurnProtectionActive: boolean;
}

export interface SessionStats {
  matchesThisSession: number;
  winsThisSession: number;
  sessionStartedAt: string | null;
  totalSessionTime: number;
  sessionCount: number;
}

export interface GASTInfo {
  value: number;
  isOverride: boolean;
  playerAvgSessionTime: number;
  churnFactorGDD: number;
}

export interface UpdateFrequency {
  winLossFactor: string;
  playerSessionTime: string;
  globalAvgSessionTime: string;
  churnFactor: string;
  churnWeight: string;
  daysSinceLastLogin: string;
  baseFactor: string;
  dayFactor: string;
}

export interface UpdateMatchdayDirectorRequest {
  currentDifficulty?: number;
  suggestedDifficulty?: number;
  churnRiskScore?: number;
  consecutiveLosses?: number;
  daysSinceLastMatch?: number;
  winRate?: number;
  recentWinRate?: number;
  reason: string;
}

export interface MatchTableStats {
  totalMatches: number;
  wins: number;
  losses: number;
  winRate: number;
}

export interface MatchdayDirectorData {
  profile: MatchdayDirectorProfile;
  directorFactors: DirectorFactors;
  opponentAdjustments: OpponentAdjustments;
  churnAssessment: ChurnAssessment;
  sessionStats: SessionStats;
  gast?: GASTInfo;
  updateFrequency?: UpdateFrequency;
  matchTableStats?: MatchTableStats;
  lastUpdated: string;
}

// Player Master & Base Stats Types
export interface PlayerMaster {
  id: number;
  playerIndex: number;
  firstName: string;
  secondName: string;
  displayName: string;
  battingHand: number;
  bowlingHand: number;
  bowlingAction: number;
  defaultPlayerType: number;
  defaultBowlerType: number;
  isWicketKeeper: boolean;
  isWinnerAlliance: boolean;
  nationality?: string;
  teamName?: string;
  teamIndex: number;
  faceAssetId: number;
  bodyAssetId?: string;
  jerseyNumber: number;
  role?: string;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface PlayerBaseStat {
  playerIndex: number;
  role: number;
  phySpeed: number;
  phyStamina: number;
  phyPower: number;
  phyReflex: number;
  phyAcrobatics: number;
  sklFootwork: number;
  sklTiming: number;
  sklRange: number;
  sklBowling: number | null;
  sklWk: number | null;
  accBat: number | null;
  accBowl: number | null;
  accField: number | null;
  cmp: number;
  createdAt: string;
  updatedAt: string;
}

export interface PlayerData extends PlayerMaster {
  baseStats?: PlayerBaseStat | null;
  // The three counts cover every card variant, or only the one in the list's `rarity` filter
  // (0=Rookie, 1=Semi-Pro, 2=Pro, 3=Legendary).
  /** Gamers who own this player now (any source); shown as "Owners". Absent until the first count finishes. */
  dropped?: number;
  /** Cards of this player gamers hold now. Absent on an older backend. */
  cards?: number;
  /** Of those cards, bought with real money (IAP). */
  boughtIap?: number;
  /** Of those cards, bought with coins. */
  boughtCoins?: number;
  /** cards − boughtIap − boughtCoins. */
  earned?: number;
  /** Gamers with this player in any HOME or AWAY squad slot. */
  inRoster?: number;
  /** Gamers with this player in one of the 11 playing-XI positions. */
  inPlayingXi?: number;
  /** The card variant the counts cover; set only when the list was filtered by `rarity`. */
  countsRarity?: number;
}

/**
 * GET /admin/cohorts/:id/spend: what a cohort's members have spent. Money = completed, real INR
 * purchases (as the cohort rules count it). Timestamps are UTC ISO strings.
 */
export interface CohortSpendReport {
  cohortId: string;
  slug: string;
  name: string;
  /** The cohort's spend rule; null when it has none. */
  spendingRange: { min: number | null; max: number | null } | null;
  summary: {
    members: number;
    payers: number;
    totalInr: number;
    purchases: number;
    medianInr: number | null;
    maxInr: number;
    boughtLast7d: number;
    seenLast7d: number;
    notSeen35d: number;
    /** Members whose spend fits the spend rule today; null without a spend rule. */
    spendFitsRule: number | null;
  };
  buckets: { key: string; label: string; members: number; inr: number }[];
  products: {
    productId: string;
    purchases: number;
    buyers: number;
    inr: number;
    avgPriceInr: number;
  }[];
  members: CohortSpendMember[];
  limit: number;
  offset: number;
}

export interface CohortSpendMember {
  userId: string;
  displayName: string | null;
  platform: string | null;
  source: string;
  assignedAt: string;
  inr: number;
  purchases: number;
  firstPurchaseAt: string | null;
  lastPurchaseAt: string | null;
  lastSeenAt: string | null;
  spendFitsRule: boolean | null;
}

/** GET /admin/cohorts/recalculate/status: a "Recalculate now" run, running or finished. */
export interface CohortRecalculationStatus {
  running: boolean;
  /** null = every dynamic cohort */
  cohortId: string | null;
  cohortName: string | null;
  startedAt: string | null;
  startedBy: string | null;
  finishedAt: string | null;
  /** Last heartbeat of a running run (every 40 s) */
  renewedAt: string | null;
  /** Players evaluated (all-cohort runs) */
  processed: number | null;
  added: number | null;
  removed: number | null;
  errors: number | null;
  /** Members after the run (single-cohort runs) */
  memberCount: number | null;
  durationMs: number | null;
  error: string | null;
}

/** GET /admin/player-data/ownership: when the Dropped / In Roster / In Playing XI counts were taken. */
export interface PlayerOwnershipStatus {
  computedAt: string | null;
  refreshing: boolean;
}

/** One division row of GET /admin/player-data/:playerIndex/distribution (all 21 sent, zeros kept). */
export interface PlayerCardDistributionDivision {
  tier: number;
  name: string;
  /** Gamers in this division who own the cricketer. */
  owners: number;
  cards: number;
  boughtIap: number;
  boughtCoins: number;
}

/** Cards by source (player_cards.source) made while the owner was in one division. */
export interface PlayerCardSourceDivision {
  tier: number;
  counts: Record<string, number>;
}

/**
 * GET /admin/player-data/:playerIndex/distribution?rarity=0..3: who owns a cricketer's cards,
 * by division, and how new cards were made. Read on demand from the admin read replica.
 */
export interface PlayerCardDistribution {
  playerIndex: number;
  displayName: string;
  /** The card variant counted; null = every variant. */
  rarity: number | null;
  computedAt: string | null;
  owners: number;
  cards: number;
  boughtIap: number;
  boughtCoins: number;
  earned: number;
  ownersByDivision: PlayerCardDistributionDivision[];
  /** Since when new cards carry a source; null when none does yet. */
  sourceTrackedSince: string | null;
  /** Only cards with a recorded source. */
  cardsBySource: Record<string, number>;
  cardsBySourceDivision: PlayerCardSourceDivision[];
}

// Custom Match — National Teams
export interface CmTeamStrength {
  batting: number;
  bowling: number;
  fielding: number;
}

export interface CmPlayerCard {
  playerIndex: number;
  isInSquad: boolean;
  faceAssetId: string;
  jerseyNumber: string;
  firstName: string;
  secondName: string;
  playerRole: number;
  isCaptain: boolean;
  isWicketKeeper: boolean;
  battingHand: string;
  bowlingType: string;
  rarity: number;
  playerRating: number;
  evolutionLevel: number;
  behaviourType: number;
  skill: number;
  physicality: number;
  accuracy: number;
  composure: number;
  skillAttributes: {
    footwork: number;
    timing: number;
    range: number;
    rangeZones: string;
    specialDelivery: number;
    swingOrSpin: number;
    appealIntuition: number;
    bowlingSkill: number;
  };
  physicalityAttributes: {
    speed: number;
    stamina: number;
    power: number;
    reflex: number;
    acrobatics: number;
  };
  accuracyAttributes: {
    shot: number;
    pitch: number;
    throwing: number;
  };
  composureAttributes: {
    errorRate: number;
    confidence: number;
    composureMeter: number;
  };
  nationality: string;
}

export interface CmNationalTeam {
  id: string;
  abbreviation: string;
  teamName: string;
  jerseyId: number;
  rank: number;
  teamRating: number;
  teamStrength: CmTeamStrength;
  playerCards: CmPlayerCard[];
  isActive: boolean;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
  updatedBy?: string | null;
}

export interface CmNationalTeamListResponse {
  data: CmNationalTeam[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

// ============ Post-launch v0.1 — Retention + Match Analytics ============

/** §3.6 — admin/analytics/revenue-breakdown response */
export interface RevenuePlatformRow {
  platform: 'google_play' | 'app_store';
  /** ISO-4217 currency code for this row. */
  currency: string;
  gross: number;
  refunded: number;
  net: number;
  txnCount: number;
}
export interface RevenuePerDayRow {
  day: string; // YYYY-MM-DD (IST)
  /** ISO-4217 currency code for this row. */
  currency: string;
  google_play: number;
  app_store: number;
  refunded: number;
}
export interface RevenueByCurrencyTotalsRow {
  currency: string;
  gross: number;
  refunded: number;
  net: number;
  txnCount?: number;
}
export interface RevenueClassificationRow {
  classification: 'real' | 'sandbox' | 'internal_tester' | 'editor' | string;
  currency: string;
  gross: number;
  refunded: number;
  net: number;
  txnCount: number;
  refundedTxnCount?: number;
}
export interface RevenueBreakdownResponse {
  /** @deprecated naive cross-currency sum — use totalRealMoneyByCurrency */
  totalRealMoney: number;
  totalRealMoneyByCurrency?: RevenueByCurrencyTotalsRow[];
  byPlatform: RevenuePlatformRow[];
  /** Per-classification × currency rows. Optional during deploy bridge. */
  byClassification?: RevenueClassificationRow[];
  perDaySeries: RevenuePerDayRow[];
  exclusions: { sandbox: number; internal_tester: number; editor: number };
  filterApplied: {
    status: string;
    purchaseClassification: string;
    platforms: string[];
    refundedSubtracted: boolean;
  };
}

/** §3.1 — admin/analytics/match-breakdown response */
export interface MatchByTypeRow {
  matchType: string;
  count: number;
  percentage: number;
}
export interface CustomMatchOversRow {
  overs: number;
  count: number;
  percentage: number;
}
export interface MatchPerDayRow {
  date: string;
  quick_play: number;
  custom_match: number;
}
export interface MatchBreakdownResponse {
  totalMatches: number;
  byMatchType: MatchByTypeRow[];
  customMatchByOvers: CustomMatchOversRow[];
  perDaySeries: MatchPerDayRow[];
}

/** §3.2 — admin/analytics/retention-funnel response */
export interface RetentionFunnelResponse {
  totalRegistered: number;
  dau: number;
  wau: number;
  mau: number;
  atRisk_7_30d: number;
  likelyChurned_30d_plus: number;
  neverLoggedInAgainAfterRegister: number;
}

/** §3.3 — admin/analytics/funnel-drop-off response */
export interface FunnelDropOffResponse {
  usersWithZeroMatches: number;
  buckets: {
    registeredLast24h: number;
    registeredLast7d: number;
    registeredOver7dAgo: number;
  };
  loggedInPattern: {
    loggedInLast24h: number;
    loggedIn_2_to_7_days: number;
    loggedIn_8_to_30_days: number;
    loggedInOver30DaysAgo: number;
    neverLoggedInAgain: number;
  };
  ftueStateBreakdown: {
    ftueCompleted: number;
    ftueIncomplete: number;
    ftueStateUnknown: number;
  };
}

/** §3.4 — cohort filter values for /admin/players?cohort=... */
export type PlayerCohort = 'no-matches' | 'quickplay-only' | 'at-risk' | 'churned';

/** §3.7 — Matchday-Director config (read + write body shape) */
export interface MatchdayDirectorRange {
  baseMin: number;
  baseMax: number;
  adjustmentFactor: number;
}

/** GDD difficulty tiers: the keys of `gddRanges.bands`. The game calls very_hard "expert". */
export type MatchdayDirectorGddTier = 'easy' | 'medium' | 'hard' | 'very_hard';

/** An offset added to the player's average, in runs per over. */
export interface MatchdayDirectorGddBandRange {
  min: number;
  max: number;
}

export interface MatchdayDirectorGddBand {
  runRate: MatchdayDirectorGddBandRange;
  economy: MatchdayDirectorGddBandRange;
}

/** Runs per over. */
export interface MatchdayDirectorGddAverages {
  runRate: number;
  economy: number;
}

/** Keys of `gddRanges.defaultAverages`: one per match length in overs, `fallback` for any other. */
export type MatchdayDirectorGddDefaultAveragesKey =
  | 'overs1'
  | 'overs3'
  | 'overs5'
  | 'overs10'
  | 'overs20'
  | 'overs30'
  | 'overs50'
  | 'fallback';

/**
 * GDD AI ranges, used only while the `matchday_director_gdd_ranges` feature flag
 * is on: the AI's run-rate / economy targets are the player's own average plus
 * the band of their dF tier, and the game's `difficulty` is that same tier.
 */
export interface MatchdayDirectorGddRanges {
  /** Easy dF < mediumFrom ≤ Medium ≤ hardAbove < Hard ≤ veryHardAbove < Very Hard. */
  tierThresholds: { mediumFrom: number; hardAbove: number; veryHardAbove: number };
  bands: Record<MatchdayDirectorGddTier, MatchdayDirectorGddBand>;
  /** A side's own average counts with at least `minBalls` balls and min ≤ average ≤ max. */
  averages: { minBalls: number; min: number; max: number };
  /** Every range sent: floor ≤ min ≤ cap − minWidth, min + minWidth ≤ max ≤ cap. */
  rangeLimits: { floor: number; minWidth: number; cap: number };
  /** Used when the player's own average doesn't count. */
  defaultAverages: Record<MatchdayDirectorGddDefaultAveragesKey, MatchdayDirectorGddAverages>;
}

export interface MatchdayDirectorConfigPayload {
  /** Legacy offsets; kept but unused while the GDD ranges flag is on. */
  runRateRange: MatchdayDirectorRange;
  /** Legacy offsets; kept but unused while the GDD ranges flag is on. */
  economyRange: MatchdayDirectorRange;
  teamRatingRange: MatchdayDirectorRange;
  winLossFactor: { defaultWinRate: number; scaleMultiplier: number };
  churnFactor: { baseFactor: number; dayThreshold: number };
  churnWeight: { divisor: number };
  difficultyFactor: { baseDifficulty: number };
  /** Absent from backends older than the GDD ranges. */
  gddRanges?: MatchdayDirectorGddRanges;
}
export interface MatchdayDirectorConfigResponse {
  config: MatchdayDirectorConfigPayload;
  /**
   * The `matchday_director_gdd_ranges` feature flag, read fresh on every call.
   * Absent from older backends, which counts as off.
   */
  gddRangesEnabled?: boolean;
  updatedBy?: string | null;
  updatedAt?: string;
  /** Scope the config was read from; null is the shared config every build inherits. */
  configVersionId?: string | null;
  /**
   * True when the selected config version has no override of its own and is
   * showing the shared config. Saving in that state creates an override, which
   * then stops tracking later edits to the shared config.
   */
  inherited?: boolean;
}

/** What kind of write produced a Matchday-Director config history entry. */
export type MatchdayDirectorConfigAction = 'update' | 'reset' | 'override_removed';

/** One field that changed; `field` is a dot path, arrays are compared and shown whole. */
export interface MatchdayDirectorConfigFieldChange {
  field: string;
  from: unknown;
  to: unknown;
}

/** One row of GET /admin/matchday-director/config/history. */
export interface MatchdayDirectorConfigChangeEntry {
  id: string;
  /** ISO timestamp. */
  changedAt: string;
  /** The admin's "First Last", falling back to their email. */
  adminName: string;
  adminEmail: string | null;
  action: MatchdayDirectorConfigAction;
  /** Null is the shared config every build inherits. */
  configVersionId: string | null;
  /** Null for entries written before a comment was required. */
  comment: string | null;
  changes: MatchdayDirectorConfigFieldChange[];
}

export interface MatchdayDirectorConfigHistoryResponse {
  /** Newest first. */
  items: MatchdayDirectorConfigChangeEntry[];
  total: number;
}

export interface MatchdayDirectorConfigHistoryParams {
  /** Omit for the shared config's history. Ignored when `all` is true. */
  configVersionId?: string | null;
  /** Every scope's history, shared and every override. */
  all?: boolean;
  /** 1-200, server default 50. */
  limit?: number;
  offset?: number;
}

/** §2.7 — Daily-deal free_drop extended rewards JSONB (admin editor adds these fields) */
export interface DailyDropBaseAmounts {
  coins: number;
  matchTokens: number;
  evoTokens: number;
  cards: number;
}
export type EvoTokensByDivision = Record<number, number>; // keys 1..21

// ============================================================
// AI Batting Diagnostics — mirrors backend admin-ai-analytics.dto.ts
// See Fixes to be done - after launch v0.1/AI-Analytics new updates/
//     AI-BATTING-DIAGNOSTICS-IMPLEMENTATION.md §6.1
// ============================================================

export interface WicketByDifficultyRow {
  aidiff: number;
  wicketType: string;
  count: number;
  pct: number;
}

export interface WicketByDivisionRow {
  division: number;
  wicketType: string;
  count: number;
}

export interface WicketLbwTierRow {
  aidiff: number;
  totalWickets: number;
  lbwCount: number;
  lbwPct: number;
}

export interface WicketByShotIdRow {
  shotId: number;
  shotName: string;
  count: number;
  lbwCount: number;
}

export interface WicketByDeliveryRow {
  deliveryTypeId: number;
  deliveryName: string;
  count: number;
}

export interface WicketsOverviewResponse {
  totalWickets: number;
  byDifficulty: WicketByDifficultyRow[];
  byDivision: WicketByDivisionRow[];
  lbwPctByTier: WicketLbwTierRow[];
  topShotIdsForLbw: WicketByShotIdRow[];
  topDeliveryTypesForWickets: WicketByDeliveryRow[];
  zoneBreakdown: Record<number, number>;
}

export interface BounceClusterCell {
  x: number;
  z: number;
  wicketType: string;
  count: number;
}

export interface BounceClustersResponse {
  gridSize: number;
  cells: BounceClusterCell[];
  totalWickets: number;
}

export interface BatsmanPositionClusterCell {
  x: number;
  z: number;
  wicketType: string;
  shotId: number | null;
  count: number;
}

export interface BatsmanPositionClustersResponse {
  gridSize: number;
  cells: BatsmanPositionClusterCell[];
  totalWickets: number;
}

export interface ShotAngleBucket {
  bucketStart: number;
  bucketEnd: number;
  count: number;
}

export interface FieldingZoneCount {
  zoneId: number;
  zoneName: string;
  count: number;
}

export interface TravelDistanceBucket {
  bucketStart: number;
  bucketEnd: number;
  count: number;
}

export interface CatchAnalysisResponse {
  totalCatches: number;
  angleBuckets: ShotAngleBucket[];
  fieldingZones: FieldingZoneCount[];
  /** Catches up to implausibleTravelMetres only. */
  travelDistanceBuckets: TravelDistanceBucket[];
  /** Catches over implausibleTravelMetres (bad position data); absent on older servers. */
  implausibleTravelCount?: number;
  implausibleTravelMetres?: number;
}

export interface PhysicsBucketRow {
  bucket: string;
  wicketType: string;
  count: number;
}

export interface DeliveryTypeRow {
  deliveryTypeId: number;
  deliveryName: string;
  wicketType: string;
  count: number;
}

export interface WicketsByPhysicsResponse {
  pace: PhysicsBucketRow[];
  spin: PhysicsBucketRow[];
  swing: PhysicsBucketRow[];
  delivery: DeliveryTypeRow[];
}

// Extras — aggregations for the five columns the headline endpoints
// don't cover (innings, over, batsmanIndex, bowlerIndex, shotType).
export interface WicketByInningsRow {
  innings: number;
  wicketType: string;
  count: number;
}
export interface WicketByOverRow {
  over: number;
  count: number;
}
export interface WicketBySquadIndexRow {
  squadIndex: number;
  wicketType: string;
  count: number;
}
export interface WicketByShotTypeRow {
  shotType: number | null;
  wicketType: string;
  count: number;
}

// ── Shot-timing aggregations (brief §13, realigned 2026-06-05) ──
export interface WicketByShotTimingZoneRow {
  shotTimingZone: number | null;
  wicketType: string;
  count: number;
}
export interface WicketByShotTimingDirectionRow {
  shotTimingDirection: number | null;
  wicketType: string;
  count: number;
}
export interface WicketByTimingValueBucketRow {
  bucket: number; // 1..10 width_bucket over signed [-1..+1]
  wicketType: string;
  count: number;
}
export interface TimingByDeliveryRow {
  deliveryTypeId: number;
  shotTimingZone: number | null;
  count: number;
}
export interface TimingByPhysicsBucketRow {
  bucket: number;
  shotTimingZone: number | null;
  count: number;
}
export interface TimingByBattingZoneRow {
  battingShotZone: number;
  shotTimingZone: number | null;
  count: number;
}

export interface WicketsExtrasResponse {
  byInnings: WicketByInningsRow[];
  byOver: WicketByOverRow[];
  byBatsmanIndex: WicketBySquadIndexRow[];
  byBowlerIndex: WicketBySquadIndexRow[];
  byShotType: WicketByShotTypeRow[];
  byShotTimingZone: WicketByShotTimingZoneRow[];
  byShotTimingDirection: WicketByShotTimingDirectionRow[];
  byTimingValueBucket: WicketByTimingValueBucketRow[];
  timingByDelivery: TimingByDeliveryRow[];
  timingByPaceBucket: TimingByPhysicsBucketRow[];
  timingBySpinBucket: TimingByPhysicsBucketRow[];
  timingBySwingBucket: TimingByPhysicsBucketRow[];
  timingByBattingZone: TimingByBattingZoneRow[];
}

// Raw wicket events — every column, every row, paginated.
export interface WicketEventRow {
  id: string;
  userId: string;
  matchId: string | null;
  createdAt: string;
  aidiff: number | null;
  division: number | null;
  innings: number;
  over: number;
  ball: number;
  wicketType: string;
  batsmanIndex: number;
  bowlerIndex: number;
  ballBounceX: number;
  ballBounceZ: number;
  shotType: number | null;
  shotId: number | null;
  shotAngleDegrees: number | null;
  wicketLocationZone: number | null;
  fielderStartPositionX: number | null;
  fielderStartPositionZ: number | null;
  fielderCatchPositionX: number | null;
  fielderCatchPositionZ: number | null;
  batsmanPositionX: number;
  batsmanPositionZ: number;
  battingShotZone: number;
  bowlingPace: number;
  bowlingSpinIntensity: number;
  bowlingSwingIntensity: number;
  deliveryTypeId: number;
  /** Signed `[-1..+1]` — sign carries early/late direction. */
  shotTimingValue: number | null;
  /** Unity TimingZone — 0=Perfect, 1=Good, 2=OK, 3=Miss. */
  shotTimingZone: number | null;
  /** Unity TimingDirection — -1=Early, 0=Center, 1=Late. */
  shotTimingDirection: number | null;
}

export interface WicketEventsResponse {
  total: number;
  limit: number;
  offset: number;
  events: WicketEventRow[];
}

// ============================================================================
// Branding — brand-collab campaign manifest
// ============================================================================

export type BrandCampaignStatus = 'draft' | 'scheduled' | 'active' | 'paused' | 'ended';

/**
 * 17 named camelCase image-slot keys the Unity client parses. Mirrors the
 * backend `BrandCampaignSpots` interface verbatim — do NOT rename a key
 * here without updating the entity, DTO, and the client build.
 */
export interface BrandCampaignSpots {
  boundaryBanner?: string;
  behindStumps?: string;
  outfieldLongBanner?: string;
  outfieldSmallBanner?: string;
  sightscreen?: string;
  awardBoard?: string;
  umpireBoard?: string;
  tossScreen?: string;
  actionReplay?: string;
  batSelection?: string;
  bowlSelection?: string;
  battingScorecard?: string;
  bowlingScorecard?: string;
  chartPanel?: string;
  targetScreen?: string;
  landingTile?: string;
  menuSponsorLogo?: string;
}

export const BRAND_CAMPAIGN_SPOT_KEYS: ReadonlyArray<keyof BrandCampaignSpots> = [
  'boundaryBanner',
  'behindStumps',
  'outfieldLongBanner',
  'outfieldSmallBanner',
  'sightscreen',
  'awardBoard',
  'umpireBoard',
  'tossScreen',
  'actionReplay',
  'batSelection',
  'bowlSelection',
  'battingScorecard',
  'bowlingScorecard',
  'chartPanel',
  'targetScreen',
  'landingTile',
  'menuSponsorLogo',
] as const;

export interface BrandCampaign {
  id: string;
  name: string;
  status: BrandCampaignStatus;
  startsAt: string;
  endsAt: string;
  spots: BrandCampaignSpots;
  createdByAdminId?: string | null;
  createdByAdminEmail?: string | null;
  updatedByAdminId?: string | null;
  updatedByAdminEmail?: string | null;
  createdAt: string;
  updatedAt: string;
  /** Config version this campaign is limited to; null = every build. */
  configVersionId?: string | null;
}

export interface BrandCampaignListResponse {
  data: BrandCampaign[];
  total: number;
  limit: number;
  offset: number;
}

// ==================== Per-build config versions ====================

export type AppVersionPlatform = 'all' | 'android' | 'ios';

/** Compact reference to a config version, embedded in an app-version row. */
export interface ConfigVersionRef {
  id: string;
  version: string;
}

/**
 * A named bundle of overrides that app versions point at. Creating one changes
 * nothing until an app version is linked to it.
 */
export interface ConfigVersion {
  id: string;
  version: string;
  description?: string | null;
  /** Null means inherit the global Force Update value. */
  minAppVersion?: string | null;
  latestAppVersion?: string | null;
  forceUpdate?: boolean | null;
  softUpdate?: boolean | null;
  updateUrl?: string | null;
  /** Client toggles overridden for builds on this config version. */
  featureFlagOverrides: Record<string, boolean>;
  usedByCount?: number;
  updatedAt: string;
}

export interface CreateConfigVersion {
  version: string;
  description?: string;
  minAppVersion?: string;
  latestAppVersion?: string;
  forceUpdate?: boolean;
  softUpdate?: boolean;
  updateUrl?: string;
  featureFlagOverrides?: Record<string, boolean>;
}

export type UpdateConfigVersion = Partial<CreateConfigVersion>;

export interface CloneConfigVersion {
  version: string;
  description?: string;
}

/** One released client build, pointing at the config version it receives. */
export interface AppVersionMapping {
  id: string;
  buildVersion: string;
  buildNumber?: number | null;
  platform: AppVersionPlatform;
  configVersionId: string;
  configVersion?: ConfigVersionRef;
  isActive: boolean;
  releasedAt?: string | null;
  note?: string | null;
  updatedAt: string;
}

export interface CreateAppVersionMapping {
  buildVersion: string;
  buildNumber?: number;
  platform?: AppVersionPlatform;
  configVersionId: string;
  isActive?: boolean;
  releasedAt?: string;
  note?: string;
}

export type UpdateAppVersionMapping = Partial<CreateAppVersionMapping>;

// ===== Group analytics reports (/admin/analytics/*) =====
// Nullable columns arrive as missing keys: the global NullRemovalInterceptor
// strips nulls, so those fields are optional here.

export interface GroupAnalyticsParams {
  from: string;
  to: string;
}

export interface SessionStatsRow {
  day: string;
  sessions: number;
  users: number;
  sessionsPerUser?: number;
  medianSeconds: number;
  p90Seconds: number;
  matchesPerSession?: number;
  timeoutShare?: number;
}

export interface MatchesPerUserRow {
  day: string;
  isPayer: boolean;
  activeUsers: number;
  matches: number;
  matchesPerUser?: number;
}

export type MatchBucket = '0' | '1-4' | '5-19' | '20-49' | '50+';

export interface CohortBreakdownRow {
  matchBucket: MatchBucket;
  divisionTier: number;
  isPayer: boolean;
  usesCustomMatch: boolean;
  users: number;
}

export interface ModeMixRow {
  matchType: string;
  overs?: number;
  matches: number;
  users: number;
  completed: number;
  tokenCost: number;
}

export interface CardDropsRow {
  matchType: string;
  divisionTier: number;
  matchOutcome: 'win' | 'draw' | 'loss';
  matches: number;
  matchesWithDrops: number;
  uniqueUsers: number;
  cards: number;
  matchesWithoutRecord: number;
}

export interface AiCompetitivenessRow {
  buildVersion: string;
  aiDifficulty: number;
  matches: number;
  avgPlayerRating?: number;
  avgOpponentRating?: number;
  avgUserFours?: number;
  avgUserSixes?: number;
  avgUserDotBalls?: number;
  avgAiFours?: number;
  avgAiSixes?: number;
  avgAiDotBalls?: number;
  matchesWithVerdict: number;
  playerWinRate?: number;
  tieRate?: number;
}

export interface IapLtvRow {
  cohortDay: string;
  cohortSize: number;
  currency?: string;
  dayIndex?: number;
  payers: number;
  revenue: number;
}

// ===== Daily report (GET /admin/analytics/daily-report) =====
// The endpoint skips null removal, so nullable fields arrive as explicit
// nulls rather than missing keys.

export interface DailyReportParams {
  /** 'YYYY-MM-DD', IST day, inclusive. */
  from: string;
  /** 'YYYY-MM-DD', IST day, exclusive. The window must be 1-92 days. */
  to: string;
}

export interface DailyReportRevenue {
  /** Store currency code; 'UNKNOWN' when the purchase had none. */
  currency: string;
  /** 2 decimals. Never summed across currencies. */
  amount: number;
}

export interface DailyReportPlayersByCompletedMatches {
  /** max(dau - players with >= 1 completed match, 0); null when dau = 0. */
  '0': number | null;
  '1': number;
  '2': number;
  '3': number;
  '4': number;
  '5+': number;
}

export interface DailyReportDay {
  /** IST calendar day, 'YYYY-MM-DD'. */
  day: string;
  newUsers: number;
  ftueCompletionsD0: number;
  ftueCompletions: number;
  /**
   * Rows 17-18 and 24-25: what that day's new users played on the same IST day.
   * Optional until every backend serving this page sends them.
   */
  d0PlayersStarted?: number;
  d0PlayersCompleted?: number;
  /** 2 decimals; null when newUsers = 0. */
  d0MatchesStartedPerUser?: number | null;
  /** 2 decimals; null when newUsers = 0. */
  d0MatchesCompletedPerUser?: number | null;
  quickPlayStarted: number;
  quickPlayCompleted: number;
  customMatchStarted: number;
  customMatchCompleted: number;
  blitzStarted: number;
  blitzCompleted: number;
  matchesStarted: number;
  matchesCompleted: number;
  dau: number;
  /** 2 decimals; null when dau = 0. */
  matchesStartedPerDau: number | null;
  /** 2 decimals; null when dau = 0. */
  matchesCompletedPerDau: number | null;
  startedAndCompletedSameDay: number;
  playersByCompletedMatches: DailyReportPlayersByCompletedMatches;
  sessions: number;
  /** Whole seconds; null when no ended session has a duration. */
  sessionMedianSeconds: number | null;
  /** Whole seconds; null when no ended session has a duration. */
  sessionP90Seconds: number | null;
  /** Sorted by currency; empty when there was no revenue. */
  iapRevenue: DailyReportRevenue[];
  payers: number;
}

export interface DailyReportResponse {
  from: string;
  /** Exclusive. */
  to: string;
  /** Every IST day in [from, to), ascending, zero-filled. */
  days: DailyReportDay[];
}

/** Rows 43-44, from the client's GA4 events in BigQuery; USD. */
export interface DailyReportAdsDay {
  day: string;
  /** null when the analytics layer has no row for the day yet. */
  adRevenueUsd: number | null;
  /** GA4's gross IAP figure (not the per-currency store revenue of row 45). */
  iapRevenueUsd: number | null;
  totalRevenueUsd: number | null;
}

export interface DailyReportAdsResponse {
  from: string;
  /** Exclusive. */
  to: string;
  /** false when BigQuery could not answer; every day is then null. */
  available: boolean;
  /** Last day in the window the analytics layer has; null when none. */
  dataThrough: string | null;
  days: DailyReportAdsDay[];
}

// ---------------------------------------------------------------------------
// Daily Engagement config (Daily Reward, spin wheel, coin bonus, streak
// freezes, loyalty). Mirrors the backend's DailyEngagementConfig
// (game-config/types/daily-engagement-config.types.ts) exactly. One document,
// read and replaced whole with GET/PUT /admin/config/daily-engagement-config.
// ---------------------------------------------------------------------------

export type DailyCurrency = 'coins' | 'evoTokens' | 'matchTokens';

export type DailyDayType =
  | 'normal'
  | 'card_chance'
  | 'guaranteed_milestone'
  | 'enhanced_milestone'
  | 'grand_milestone';

/** Backend rarity 0 Common (rookie) … 3 Legendary (legendary). */
export type DailyCardRarity = 'rookie' | 'semi_pro' | 'pro' | 'legendary';

export interface DailyCardSpec {
  rarity: DailyCardRarity;
  count: number;
}

/** amount = base + perStep × floor(divisionTier / divisor). */
export interface DailyBaseFormula {
  base: number;
  perStep: number;
  divisor: number;
}

export interface DailyMultiplierBand {
  fromDay: number;
  toDay: number;
  multiplier: number;
}

export interface DailyDayConfig {
  cycleDay: number;
  dayType: DailyDayType;
  /** The one currency a normal / card_chance day grants; null otherwise. */
  currency: DailyCurrency | null;
  /** Cards for guaranteed_milestone and grand_milestone (1..3 in total). */
  cards: DailyCardSpec[];
}

export type DailySpinSegment = DailyCardRarity | 'no_card';

export type DailySpinBandName = 'low' | 'mid' | 'high';

/** Percent weights per segment; they sum to 100. */
export type DailySpinOdds = Record<DailySpinSegment, number>;

export interface DailySpinBand {
  band: DailySpinBandName;
  fromDivision: number;
  toDivision: number;
  odds: DailySpinOdds;
}

export interface DailySpinConfig {
  /** Contiguous, ascending, covering divisions 1..21. */
  bands: DailySpinBand[];
  /** RV re-spin: shares (percent, sum 100) of the freed half of No Card. */
  respinSplit: Record<DailyCardRarity, number>;
  /** Consecutive No Card results (re-spins count) before a guaranteed card. */
  guaranteeAfterMisses: number;
}

export interface DailyFreezeConfig {
  baseCapacity: number;
  purchaseCostMatchTokens: number;
  freeFreezes: number;
}

export type DailyLoyaltyTierName = 'bronze' | 'silver' | 'gold' | 'platinum' | 'legend';

export interface DailyVaultOption {
  optionId: string;
  reward: {
    coins: number;
    evoTokens: number;
    matchTokens: number;
    cards: DailyCardSpec[];
  };
  streakFreezes: number;
}

export interface DailyLoyaltyTier {
  tier: DailyLoyaltyTierName;
  threshold: number;
  freezeCapacityBonus: number;
  /** Exactly 3; the player opens one, once. */
  options: DailyVaultOption[];
}

export interface DailyLoyaltyConfig {
  /** Exactly 5 tiers, bronze..legend, thresholds strictly ascending. */
  tiers: DailyLoyaltyTier[];
}

export interface DailyEngagementConfig {
  schemaVersion: 1;
  baseRewards: Record<DailyCurrency, DailyBaseFormula>;
  multiplierBands: DailyMultiplierBand[];
  caps: Record<DailyCurrency, number>;
  days: DailyDayConfig[];
  enhancedBonus: {
    coinsPercent: number;
    evoTokens: number;
    matchTokens: number;
  };
  /** null = a random Legendary on Day 28; else a player master playerIndex. */
  day28CardId: number | null;
  spin: DailySpinConfig;
  /** RV coin bonus: percent of the day's granted coins, after the cap. */
  coinBonusPercent: number;
  freezes: DailyFreezeConfig;
  loyalty: DailyLoyaltyConfig;
}

/**
 * GET and PUT response. updatedAt is the shared config row's timestamp (any
 * column on that row moves it), not a Daily Engagement "last changed" time.
 */
export interface DailyEngagementConfigResponse {
  dailyEngagementConfig: DailyEngagementConfig;
  updatedAt?: string;
}

/**
 * A player's Daily Engagement fast clock (staging only; the server answers
 * 404 in production). Mirrors admin-daily-engagement-fast-clock.dto.ts.
 */
export interface DailyEngagementFastClock {
  userId: string;
  /** Real seconds per IST day; null = the real clock. */
  secondsPerDay: number | null;
  /** Real instant the current speed started. */
  anchorUtc: string | null;
  /** The time the player's Daily Engagement rules run on now. */
  virtualNowUtc: string;
  /** True when this call switched the clock off and reset the player's state. */
  reset: boolean;
}

// Today dashboard (GET /admin/analytics/today). Mirrors TodayResponse in
// wcc4-backend src/modules/analytics/today-stats-query.service.ts.
export type TodayCounterField =
  | 'new_users'
  | 'logins'
  | 'players'
  | 'matches_started'
  | 'matches_completed'
  | 'runs'
  | 'purchases'
  | 'revenue_inr'
  | 'ads_watched';
export type TodayMetric = TodayCounterField | 'payers';
export type TodayFreshness = 'ok' | 'stale' | 'dead';

/**
 * Cumulative totals at the end of one IST hour. `hourly.today` has one point
 * per hour up to and including the current one; `hourly.yesterday` has 24.
 * Both arrays are empty when Redis could not be read (freshness is all `dead`
 * then), rather than carrying invented zero points.
 */
export interface HourPoint {
  hour: number;
  cum: Record<TodayMetric, number>;
}

export interface TodayAdsNetwork {
  network: string;
  adType: string;
  usd: number;
  impressions: number;
}

export interface TodayResponse {
  /** IST day, YYYY-MM-DD. */
  day: string;
  asOf: string;
  /**
   * HH:MM IST from which today's live counters are complete; null when they
   * cover the whole day so far. Set when the recorder's first flush today was
   * after 00:05, or an elapsed hour has no `flushed` presence field (the time
   * is then the end of the last such hour). Applies to the counter-only fields
   * (logins, new_users, matches_*, runs, ads_watched).
   */
  countingSince: string | null;
  /**
   * HH:MM IST like `countingSince`, for the reconciled fields (players,
   * purchases, revenue_inr, payers): hours at or before the last hour the
   * replica reconcile wrote (`today:reconciled:{day}`) count as covered, and
   * the since marker only counts when it is after the end of that hour. Same
   * value as `countingSince` when the reconcile has not run today.
   */
  countingSinceReconciled: string | null;
  /**
   * HH:MM IST, `countingSince`'s rule applied to yesterday over its hours
   * 00..current hour (what `yesterdaySameTime` uses): yesterday's since marker
   * after 00:05, or the end of the last of those hours without `flushed`.
   * null when yesterday is fully covered up to this time of day.
   */
  yesterdayCountingSince: string | null;
  /**
   * HH:MM IST, `yesterdayCountingSince` for the reconciled fields, using
   * yesterday's `today:reconciled:{day}` marker ('23' = fully covered).
   */
  yesterdayCountingSinceReconciled: string | null;
  activeNow: {
    value: number | null;
    peak: number | null;
    /** HH:MM IST. */
    peakAt: string | null;
  };
  totals: Record<TodayMetric, number>;
  yesterdaySameTime: Record<TodayMetric, number>;
  hourly: { today: HourPoint[]; yesterday: HourPoint[] };
  money: {
    iapGrossInr: number;
    iapNetInr: number;
    adsInr: number | null;
    totalInr: number;
    netShare: number;
    usdInr: number;
  };
  ads: null | {
    asOf: string;
    usd: number;
    estimatedUsd: number;
    impressions: number;
    byNetwork: TodayAdsNetwork[];
    /**
     * Estimated INR per rewarded ad granted today. null when no ad was granted
     * yet, or while `countingSince` is set (grants before it were not counted,
     * so a per-grant rate would be inflated).
     */
    inrPerAd: number | null;
  };
  freshness: Record<TodayCounterField | 'ads', TodayFreshness>;
}

// BigQuery Insights (GET /admin/analytics/bq-insights?days=). Mirrors
// wcc4-backend src/modules/analytics/bq-insights.types.ts. Dates are GA4 days
// (IST) 'YYYY-MM-DD'; rates are fractions (0.25 = 25%); null = not known yet.
export interface BqKpiDay {
  date: string;
  dau: number;
  newUsers: number;
  minutesPerDau: number | null;
  matchesPerDau: number | null;
  payers: number;
  payerRate: number | null;
  iapRevenueUsd: number;
  adRevenueUsd: number;
  revenueUsd: number;
  arpdauUsd: number | null;
  /** Not fill rate: the client logs ad requests for one ad flow only. */
  adImpressionsPerDau: number | null;
  /** app_exception events per DAU (Crashlytics is not exported). */
  exceptionsPerDau: number | null;
  uninstallers: number;
  d1Retention: number | null;
  d7Retention: number | null;
  d7LtvUsd: number | null;
}

export interface BqRetentionPoint {
  cohortDate: string;
  dayN: number;
  cohortSize: number;
  retentionRate: number | null;
  ltvUsd: number | null;
}

export interface BqFtueStep {
  step: string;
  users: number;
  medianSec: number | null;
}

/** Install-day tutorial funnel of the latest install day, most-reached step first. */
export interface BqFtue {
  installDate: string;
  cohortUsers: number;
  steps: BqFtueStep[];
}

export interface BqSegmentRow {
  date: string;
  dimension: string;
  segment: string;
  newUsers: number;
  d1Retention: number | null;
  uninstallRate: number | null;
  ftueRate: number | null;
  dau: number;
  payerRate: number | null;
  arpdauUsd: number | null;
}

export type BqSeverity = 'critical' | 'warning' | 'info' | 'good';

export interface BqInsight {
  date: string;
  kind: string;
  severity: BqSeverity;
  title: string;
  detail: string;
  metric: number | null;
  baseline: number | null;
}

export interface BqModelStatus {
  model: string;
  status: 'trained' | 'waiting' | 'failed';
  trainedAt: string | null;
  trainRows: number | null;
  rocAuc: number | null;
  accuracy: number | null;
  note: string | null;
  /**
   * Whether score_players uses this model: trained with ROC AUC >= 0.65 on
   * held-out players. A weaker model is shown but its scores stay empty.
   */
  usedForScores: boolean;
}

/** Scores of the latest scored day. Likely buyer = purchase probability >= 0.3. */
export interface BqScoreSummary {
  asOf: string;
  scored: number;
  churnHigh: number;
  churnMedium: number;
  churnLow: number;
  likelyBuyers: number;
  newInstalls: number;
  /** New installs the D1 model expects not to return (probability < 0.5). */
  newInstallsLikelyLost: number;
  /** Most likely first product among likely buyers. */
  offers: { productId: string; players: number }[];
}

export interface BqDriver {
  model: string;
  feature: string;
  attribution: number;
}

export type BqSection =
  | 'kpis'
  | 'retention'
  | 'ftue'
  | 'segments'
  | 'insights'
  | 'models'
  | 'scores'
  | 'drivers'
  | 'versions';

export interface BqInsightsResponse {
  generatedAt: string;
  /** Last GA4 day with KPIs; null when none in the range. */
  dataThrough: string | null;
  days: number;
  kpis: BqKpiDay[];
  retention: BqRetentionPoint[];
  ftue: BqFtue | null;
  segments: BqSegmentRow[];
  insights: BqInsight[];
  models: BqModelStatus[];
  scores: BqScoreSummary | null;
  drivers: BqDriver[];
  /** Sections not answered this time (BigQuery not configured, over budget, or an error). */
  unavailable: BqSection[];
  /**
   * Platform and app version (null = all versions) the page is filtered to. KPIs, trends, retention and the FTUE
   * funnel follow them; segments, insights, models and scores are always the whole game.
   */
  platform: BqPlatform;
  version: string | null;
  /** App versions seen in the range on `platform`, most players first (the filter's choices). */
  versions: BqVersionOption[];
}

export type BqPlatform = 'all' | 'android' | 'ios';

export interface BqFilter {
  platform: BqPlatform;
  /** null = all versions. */
  version: string | null;
}

export interface BqVersionOption {
  version: string;
  /** Sum of the daily active players on this version over the range. */
  playerDays: number;
}

// ---------------------------------------------------------------------------
// FTUE & Progression page: GET /admin/analytics/ftue-progression.
// Mirrors wcc4-backend/src/modules/analytics/ftue-progression.types.ts (the response
// parts only; the histogram and rollup-row types are server-only). Keep the two in step.
// Dates are IST days, 'YYYY-MM-DD'. Rates and shares are fractions (0.25 = 25%).
// null = not known (nobody reached it, or the base is empty).
// ---------------------------------------------------------------------------

export type FtuePlatform = 'all' | 'android' | 'ios';

/** What the page asks for. `version` is 'all', 'unknown' or a real version such as '0.4.4'. */
export interface FtueFilter {
  from: string;
  to: string;
  version: string;
  platform: FtuePlatform;
}

export interface FtueFunnelStep {
  step: number;
  label: string;
  /** Players who reached this step or any later one; never above the previous step. */
  players: number;
  /** Players who logged exactly this step's event (raw count, for checking). */
  logged: number;
  /** players / installs. */
  shareOfInstalls: number | null;
  /** Share of the previous step's players who did not reach this step. */
  lostHere: number | null;
  /** Median minutes from install to this step. */
  medianMinutes: number | null;
}

/** Google login failures: players who hit one, and those of them who never got in. */
export interface FtueLoginTrouble {
  failed: number;
  neverLoggedIn: number;
}

/** Matches played to reach a level: 25th percentile, median, 75th percentile. */
export interface FtueMatchSpread {
  p25: number;
  median: number;
  p75: number;
}

export interface FtueLevelRow {
  level: number;
  /** Feature the level unlocks, if any. */
  unlock: string | null;
  reached: number;
  /** reached / base. */
  reachShare: number | null;
  matches: FtueMatchSpread | null;
  /** Median minutes of play to reach the level. */
  playMinutes: number | null;
  /** Median hours from tutorial end to reaching the level. */
  elapsedHours: number | null;
}

export interface FtueProgression {
  /** 'final' when 200+ finishers have had 7 days; otherwise 'early' over everyone. */
  status: 'final' | 'early';
  /** Median days of data the finishers have had (early only). */
  earlyDays: number | null;
  /** Players who finished the tutorial, whatever their age. */
  finishers: number;
  /** Finishers the level shares are measured against. */
  base: number;
  levels: FtueLevelRow[];
}

export interface FtueProgressionView {
  filter: FtueFilter;
  installs: number;
  funnel: FtueFunnelStep[];
  loginTrouble: FtueLoginTrouble;
  progression: FtueProgression;
  /**
   * Install days from this date (IST, 'YYYY-MM-DD') have had less than 24 hours of data,
   * so later funnel steps are still filling; null when the window has none.
   */
  partialFrom: string | null;
}

export type FtueSection =
  | 'versions'
  | 'funnel'
  | 'progression'
  | 'compare-funnel'
  | 'compare-progression';

export interface FtueVersion {
  version: string;
  installs: number;
}

export interface FtueProgressionResponse {
  generatedAt: string;
  dataThrough: string | null;
  minPlayers: number;
  versions: FtueVersion[];
  /** Empty data plus an entry in `unavailable` when its queries failed. */
  view: FtueProgressionView;
  compare: FtueProgressionView | null;
  unavailable: FtueSection[];
}

/** Query string of GET /admin/analytics/ftue-progression; every key is optional. */
export interface FtueQuery {
  from?: string;
  to?: string;
  version?: string;
  platform?: FtuePlatform;
  compareFrom?: string;
  compareTo?: string;
  compareVersion?: string;
  comparePlatform?: FtuePlatform;
}

// ---- Product Metrics (GET /admin/analytics/product-metrics) ----
// Mirrors wcc4-backend/src/modules/analytics/product-metrics.types.ts. Dates are IST days; rates are
// fractions; null = not known (an empty base). A window of several days counts player-days: "DAU" is
// the average per day, and every per-DAU figure divides by the summed DAU of the window.

export type PmPlatform = 'all' | 'android' | 'ios';
/** ALL = every mode; QP = Quick Play, CM = Custom Match, BS = Blitz Showdown. */
export type PmMode = 'ALL' | 'QP' | 'CM' | 'BS';

export interface PmFilter {
  from: string;
  to: string;
  version: string;
  platform: PmPlatform;
}

export interface PmOverview {
  days: number;
  dauTotal: number;
  dau: number | null;
  d0Total: number;
  d0Dau: number | null;
  sessions: number;
  engagementMinutes: number;
  sessionsPerDau: number | null;
  minutesPerDau: number | null;
  minutesPerSession: number | null;
}

export interface PmStumpStep {
  key: 'dau' | 'stump1' | 'stump2' | 'stump3';
  label: string;
  claimed: number;
  completed: number;
  claimedOfDau: number | null;
  claimedOfPrevious: number | null;
  completedOfDau: number | null;
  completedOfPrevious: number | null;
}

export interface PmStumpFunnel {
  steps: PmStumpStep[];
  loginClaimed: number;
}

export interface PmDayNValue {
  dayN: number;
  completed: number;
  dau: number;
  completedPerDau: number | null;
}

export interface PmModeColumn {
  mode: PmMode;
  started: number;
  startedD0: number;
  resumed: number;
  completed: number;
  completedD0: number;
  startedPerDau: number | null;
  completedPerDau: number | null;
  startToComplete: number | null;
  players: number;
  oversPlayed: number;
  oversPerPlayer: number | null;
  byDayN: PmDayNValue[];
}

export interface PmRetention {
  d1Installs: number;
  d1Retained: number;
  d1: number | null;
  d3Installs: number;
  d3Retained: number;
  d3: number | null;
}

export interface PmDayRow {
  date: string;
  dau: number;
  d0Dau: number;
  sessionsPerDau: number | null;
  minutesPerDau: number | null;
  started: number;
  completed: number;
  d1: number | null;
}

export type PmSection = 'versions' | 'daily' | 'matches' | 'retention';

export interface PmVersion {
  version: string;
  /** Player-days in the last 30 days. */
  dau: number;
}

export interface ProductMetricsResponse {
  generatedAt: string;
  dataThrough: string | null;
  filter: PmFilter;
  versions: PmVersion[];
  overview: PmOverview;
  stumps: PmStumpFunnel;
  matches: PmModeColumn[];
  retention: PmRetention;
  byDay: PmDayRow[];
  /** Sections whose query failed; they answer with empty data. */
  unavailable: PmSection[];
}

/** Query string of GET /admin/analytics/product-metrics; every key is optional. */
export interface PmQuery {
  from?: string;
  to?: string;
  version?: string;
  platform?: PmPlatform;
}

// ---- Monetization (GET /admin/monetization/{tab}) ----
// Mirror of wcc4-backend src/modules/monetization-data/monetization-dashboard.types.ts.
// Dates are IST days, 'YYYY-MM-DD'; a window is `from` (included) to `to` (excluded).
// Rates are fractions (0.25 = 25%). null = not known (a zero denominator, a column the
// rollup has not filled); the page tells it from zero. User counts are never summed
// across rows: the tables that collapse days carry none, segments carry player-days.

export type MonetizationTab =
  | 'summary'
  | 'skus'
  | 'banners'
  | 'offers'
  | 'ads'
  | 'economy'
  | 'segments'
  | 'experiments';

/** IST YYYY-MM-DD, `to` exclusive. */
export interface MonetizationQuery {
  from: string;
  to: string;
  /** skus, banners, ads and segments only. */
  country?: string;
  /** experiments only. */
  experimentKey?: string;
}

export interface MonetizationResult<T> {
  /** null when the query was skipped (see `skipped`). */
  data: T | null;
  /** Why there is no data (not configured, over the byte cap, failed); never cached. */
  skipped: string | null;
  /** When the answer was computed (ISO); a cached answer keeps its original time. */
  cachedAt: string;
}

/** One kpi_daily row (through v_kpi_daily). */
export interface KpiDay {
  date: string;
  dau: number;
  /** Distinct payers of the day, always the GA4 count (payersBackend is the backend count). */
  payers: number | null;
  /** Distinct payers from the backend purchases export; null on a day the export does not cover. */
  payersBackend: number | null;
  payerRate: number | null;
  /** Gross, before refunds. */
  iapRevenueUsd: number | null;
  iapRevenueInr: number | null;
  adRevenueUsd: number | null;
  /** IAP + ads, net of refunds. */
  revenueUsd: number | null;
  /** revenueUsd / dau. */
  arpdauUsd: number | null;
  refundsUsd: number | null;
  adImpressions: number | null;
  cartUsers: number | null;
  cartBuyers: number | null;
  d1Retention: number | null;
  uninstallers: number | null;
  iapRevenueSource: 'backend' | 'ga4';
}

/** v_payer_repeat: players whose first purchase was on `firstPurchaseDate`. */
export interface RepeatCohort {
  firstPurchaseDate: string;
  payers: number;
  repeatPayers7d: number;
  repeatRate7d: number | null;
  /** false while the cohort's 7th day is past the backend export: the rate can still rise. */
  complete: boolean;
}

/** An 'alert' row of dq_daily. */
export interface DqAlert {
  date: string;
  checkName: string;
  subject: string | null;
  value: number | null;
  threshold: number | null;
  detail: string | null;
}

/** A store_prices_backend row whose sync_status is 'mismatch'. */
export interface StoreMismatch {
  sku: string;
  store: string;
  syncStatus: string;
  catalogueInr: number | null;
  storePriceInr: number | null;
  storeCurrency: string | null;
  mismatchFields: string[];
  lastSyncedAt: string | null;
}

/** Weighted over the window: every ratio is Σ numerator / Σ denominator, never a mean of daily rates. */
export interface KpiTotals {
  /** Σ net revenue / Σ dau. */
  arpdauUsd: number | null;
  /** Σ payers (backend payers on backend days) / Σ dau. */
  payerRate: number | null;
  /** Σ iap INR / Σ backend payers, backend days only. */
  arppuInr: number | null;
  /** Σ cart buyers / Σ cart users. */
  cartToPurchase: number | null;
  /** Σ ad impressions / Σ dau. */
  adImpressionsPerDau: number | null;
  /** Σ repeat payers / Σ payers, complete cohorts only. */
  repeatPayers7d: number | null;
  /** Σ refunds / Σ gross IAP, backend days only. */
  refundShare: number | null;
}

export interface MonetizationFreshness {
  /** MAX(build_log.shard_date): the last GA4 day built. */
  gaBuiltThrough: string | null;
  /** Last IST day of backend purchases loaded; null until the export cron has run once. */
  purchasesExportedThrough: string | null;
  /** MAX(score_summary.as_of) of the last 14 days. */
  scoresAsOf: string | null;
  /** The build is older than IST today - 2, or an export exists and is older than IST today - 2. */
  stale: boolean;
}

export interface MonetizationSummary {
  days: KpiDay[];
  totals: KpiTotals;
  repeat: RepeatCohort[];
  dq: DqAlert[];
  storeSyncMismatches: StoreMismatch[];
  freshness: MonetizationFreshness;
}

/**
 * Per SKU over the window. `days` is the days with a row; `backendDays` those that carry
 * backend columns: a backend sum covers only those days.
 */
export interface SkuRow {
  productId: string;
  days: number;
  backendDays: number;
  carts: number | null;
  iapDetails: number | null;
  purchases: number | null;
  /** GA4 Play gross price, before refunds. */
  revenueUsd: number | null;
  cancels: number | null;
  failed: number | null;
  backendPurchases: number | null;
  backendRevenueInr: number | null;
  backendRevenueUsd: number | null;
  refunds: number | null;
  refundsUsd: number | null;
}

/** The home popup (DeeplinkAds), per slot over the window. slot 'unknown' is 0.4.x. No product_id. */
export interface BannerRow {
  slot: string;
  shown: number | null;
  closed: number | null;
  clicked: number | null;
  iapSuccess: number | null;
  iapCancelled: number | null;
  iapFailed: number | null;
}

/** Offer card funnel per offer x trigger x variant over the window (0.5 builds; empty before). */
export interface OfferRow {
  offerId: string;
  trigger: string;
  variant: string;
  shown: number | null;
  clicked: number | null;
  purchaseStarted: number | null;
  purchased: number | null;
  /** Always null until the backend offer-instance table exists. */
  backendPurchased: number | null;
  backendRevenueInr: number | null;
}

/** Ads per placement over the window. */
export interface AdPlacementRow {
  placement: string;
  requests: number | null;
  impressions: number | null;
  fails: number | null;
  rewards: number | null;
  revenueUsd: number | null;
  /** revenueUsd / impressions x 1000; null with no impression. */
  ecpmUsd: number | null;
}

/** Soft currency per currency x direction x source/sink x paid over the window. */
export interface EconomyRow {
  currency: string;
  /** 'Earned' or 'Spent'. */
  direction: string;
  sourceSink: string;
  paid: boolean;
  /** Sum of the amount param; events without one add nothing, see eventsNoAmount. */
  amount: number | null;
  events: number | null;
  /** Events with no amount param (store spends on 0.4.x). */
  eventsNoAmount: number | null;
}

/**
 * Per spender tier over the window. playerDays and payerDays are summed over the days of the
 * window (a player active on 3 days counts 3 times); never a distinct count.
 * arpdauUsd is GROSS of refunds ((iap + ad) / playerDays), unlike the summary's ARPDAU.
 */
export interface SegmentRow {
  spenderTier: string;
  days: number;
  backendDays: number;
  playerDays: number | null;
  payerDays: number | null;
  purchases: number | null;
  iapRevenueUsd: number | null;
  adRevenueUsd: number | null;
  adImpressions: number | null;
  /** Gross of refunds. */
  arpdauUsd: number | null;
}

/**
 * One variant of an experiment, over all the assignments of the last 90 days (the window does
 * not apply). The difference and interval columns are null on the control row and when the
 * standard error is 0.
 */
export interface ExperimentRow {
  experimentKey: string;
  variant: string;
  users: number;
  payers: number;
  payerRate: number | null;
  arpdauUsd: number | null;
  d1Retention: number | null;
  d1Users: number | null;
  d7Retention: number | null;
  d7Users: number | null;
  isControl: boolean;
  payerRateDiff: number | null;
  diffCiLow: number | null;
  diffCiHigh: number | null;
  d1RetentionDiff: number | null;
}
